import { useState, useEffect, useRef, useCallback, memo, useMemo, createContext, useContext } from "react";
import { useParams, useLocation, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { type Message, type Session, type Class } from "@shared/schema";
import { format } from "date-fns";
import {
  Send, Heart, ThumbsUp, Sparkles, Star, Flame,
  ArrowLeft, Video, Users, MessageSquare,
  StopCircle, Trash2, AlertCircle, Loader2,
  Subtitles, Languages, ChevronDown,
  MessageCircle, X, ChevronUp, Circle,
  PanelRightClose, PanelRightOpen,
  Maximize, Minimize, Share2, Copy, Check,
  RefreshCw, LogIn, UserPlus, Volume2, VolumeX,
  UserCircle, AlertTriangle, FlipHorizontal2, ArrowDown, Zap,
  MicOff, UserX, ShieldAlert
} from "lucide-react";
import { LiveBroadcastIcon } from "@/components/live-broadcast-icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  LiveKitRoom,
  VideoTrack,
  useTracks,
  RoomAudioRenderer,
  TrackToggle,
  MediaDeviceMenu,
  isTrackReference,
} from "@livekit/components-react";
import type { TrackReference } from "@livekit/components-react";
import "@livekit/components-styles";
import { Track, ConnectionState, VideoPresets, ScreenSharePresets, RoomOptions, RoomEvent, DisconnectReason } from "livekit-client";
import { useRoomContext, useConnectionState } from "@livekit/components-react";

interface CohostInfo {
  userId: string;
  userName: string;
  avatarUrl: string | null;
  status: string;
}

interface SessionState {
  session: Session;
  class: Class;
  provider: { name: string; id: string };
  messages: (Message & { userName: string })[];
  viewerCount: number;
  cohosts: CohostInfo[];
  recording?: {
    isRecording: boolean;
    recordingId: string | null;
    startedAt: string | null;
  };
  shareUrl?: string | null;
  permissions: {
    canChat: boolean;
    canReact: boolean;
    canModerate: boolean;
    canEndSession: boolean;
    isOwner: boolean;
    isCohost?: boolean;
    cohostStatus?: string | null;
    canKickUsers?: boolean;
    canMuteUsers?: boolean;
  };
}

interface LiveKitToken {
  token: string;
  url: string;
  roomName: string;
  permissions?: { canPublish: boolean; isCohost: boolean; isOwner: boolean };
}

const REACTION_TYPES = [
  { type: "heart", icon: Heart, color: "text-red-500" },
  { type: "thumbsup", icon: ThumbsUp, color: "text-blue-500" },
  { type: "sparkles", icon: Sparkles, color: "text-yellow-500" },
  { type: "star", icon: Star, color: "text-amber-500" },
  { type: "fire", icon: Flame, color: "text-orange-500" },
];

interface PerformanceModeContextType {
  performanceMode: boolean;
  setPerformanceMode: (enabled: boolean) => void;
  applyPerformanceMode: (enabled: boolean) => void;
  poorPerformanceDetected: boolean;
  setPoorPerformanceDetected: (detected: boolean) => void;
}

const PerformanceModeContext = createContext<PerformanceModeContextType>({
  performanceMode: false,
  setPerformanceMode: () => {},
  applyPerformanceMode: () => {},
  poorPerformanceDetected: false,
  setPoorPerformanceDetected: () => {},
});

const usePerformanceMode = () => useContext(PerformanceModeContext);

const PRE_WARMED_AUDIO_CTX_REF: { current: AudioContext | null } = { current: null };

function preWarmAudioContext() {
  if (!PRE_WARMED_AUDIO_CTX_REF.current || PRE_WARMED_AUDIO_CTX_REF.current.state === "closed") {
    try {
      PRE_WARMED_AUDIO_CTX_REF.current = new AudioContext({ sampleRate: 48000 });
      if (PRE_WARMED_AUDIO_CTX_REF.current.state === "suspended") {
        PRE_WARMED_AUDIO_CTX_REF.current.resume().catch(() => {});
      }
    } catch {}
  }
  return PRE_WARMED_AUDIO_CTX_REF.current;
}

function ConnectionStats() {
  const room = useRoomContext();
  const connectionState = useConnectionState();
  const { performanceMode, applyPerformanceMode, setPoorPerformanceDetected } = usePerformanceMode();
  const [stats, setStats] = useState<{
    rtt: number | null;
    jitter: number | null;
    packetsLost: number | null;
    bitrate: number | null;
    connectionTime: number | null;
    state: string;
    framesDropped: number | null;
    framesReceived: number | null;
  }>({ rtt: null, jitter: null, packetsLost: null, bitrate: null, connectionTime: null, state: "connecting", framesDropped: null, framesReceived: null });
  const connectStart = useRef(performance.now());
  const [showStats, setShowStats] = useState(false);
  const [showPerfSuggestion, setShowPerfSuggestion] = useState(false);
  const highRttCountRef = useRef(0);
  const prevFramesDroppedRef = useRef(0);
  const prevFramesReceivedRef = useRef(0);
  const perfSuggestionDismissedRef = useRef(false);

  const RTT_THRESHOLD = 150;
  const HIGH_RTT_CONSECUTIVE_THRESHOLD = 3;
  const FRAME_DROP_RATE_THRESHOLD = 0.05;

  useEffect(() => {
    if (connectionState === ConnectionState.Connected && stats.connectionTime === null) {
      const elapsed = Math.round(performance.now() - connectStart.current);
      setStats(prev => ({ ...prev, connectionTime: elapsed, state: "connected" }));
    } else if (connectionState === ConnectionState.Reconnecting) {
      setStats(prev => ({ ...prev, state: "reconnecting" }));
    } else if (connectionState === ConnectionState.Disconnected) {
      setStats(prev => ({ ...prev, state: "disconnected" }));
    }
  }, [connectionState]);

  useEffect(() => {
    if (!room) return;
    const interval = setInterval(async () => {
      try {
        const participants = Array.from(room.remoteParticipants.values());
        const localPub = room.localParticipant;

        let totalRtt = 0;
        let totalJitter = 0;
        let totalLost = 0;
        let totalBitrate = 0;
        let count = 0;
        let framesDropped = 0;
        let framesReceived = 0;

        for (const p of participants) {
          for (const pub of Array.from(p.trackPublications.values())) {
            if (pub.track) {
              const trackStats = await pub.track.getRTCStatsReport();
              if (trackStats) {
                trackStats.forEach((report: any) => {
                  if (report.type === "inbound-rtp" && report.kind === "video") {
                    if (report.jitter !== undefined) totalJitter += report.jitter * 1000;
                    if (report.packetsLost !== undefined) totalLost += report.packetsLost;
                    if (report.framesDropped !== undefined) framesDropped += report.framesDropped;
                    if (report.framesReceived !== undefined) framesReceived += report.framesReceived;
                    count++;
                  }
                  if (report.type === "candidate-pair" && report.currentRoundTripTime !== undefined) {
                    totalRtt += report.currentRoundTripTime * 1000;
                  }
                });
              }
            }
          }
        }

        for (const pub of Array.from(localPub.trackPublications.values())) {
          if (pub.track) {
            const trackStats = await pub.track.getRTCStatsReport();
            if (trackStats) {
              trackStats.forEach((report: any) => {
                if (report.type === "outbound-rtp" && report.kind === "video") {
                  if (report.bytesSent !== undefined && report.timestamp) {
                    totalBitrate = Math.round((report.bytesSent * 8) / (report.timestamp / 1000) / 1000);
                  }
                  if (report.framesEncoded !== undefined) framesReceived += report.framesEncoded;
                }
                if (report.type === "candidate-pair" && report.currentRoundTripTime !== undefined) {
                  totalRtt = report.currentRoundTripTime * 1000;
                }
              });
            }
          }
        }

        const currentRtt = count > 0 || totalRtt > 0 ? Math.round(totalRtt) : null;

        if (currentRtt !== null && currentRtt > RTT_THRESHOLD) {
          highRttCountRef.current++;
        } else {
          highRttCountRef.current = Math.max(0, highRttCountRef.current - 1);
        }

        const deltaDropped = Math.max(0, framesDropped - prevFramesDroppedRef.current);
        const deltaReceived = Math.max(0, framesReceived - prevFramesReceivedRef.current);
        const deltaTotal = deltaDropped + deltaReceived;
        const dropRate = deltaTotal > 0 ? deltaDropped / deltaTotal : 0;
        prevFramesDroppedRef.current = framesDropped;
        prevFramesReceivedRef.current = framesReceived;

        const isPoorPerformance =
          highRttCountRef.current >= HIGH_RTT_CONSECUTIVE_THRESHOLD ||
          dropRate > FRAME_DROP_RATE_THRESHOLD;

        setPoorPerformanceDetected(isPoorPerformance);

        if (isPoorPerformance && !performanceMode && !perfSuggestionDismissedRef.current) {
          setShowPerfSuggestion(true);
        }

        setStats(prev => ({
          ...prev,
          rtt: currentRtt ?? prev.rtt,
          jitter: count > 0 ? Math.round(totalJitter / count) : prev.jitter,
          packetsLost: count > 0 ? totalLost : prev.packetsLost,
          bitrate: totalBitrate > 0 ? totalBitrate : prev.bitrate,
          framesDropped: framesDropped > 0 ? framesDropped : prev.framesDropped,
          framesReceived: framesReceived > 0 ? framesReceived : prev.framesReceived,
        }));
      } catch {}
    }, 5000);
    return () => clearInterval(interval);
  }, [room, performanceMode, setPoorPerformanceDetected]);

  const { t } = useTranslation();

  const handleEnablePerfMode = useCallback(() => {
    applyPerformanceMode(true);
    setShowPerfSuggestion(false);
  }, [applyPerformanceMode]);

  const handleDismissPerfSuggestion = useCallback(() => {
    setShowPerfSuggestion(false);
    perfSuggestionDismissedRef.current = true;
  }, []);

  const dotColor = stats.state === "connected" ? "bg-green-400" : stats.state === "reconnecting" ? "bg-yellow-400" : "bg-red-400";
  const stateColor = stats.state === "connected" ? "text-green-400" : stats.state === "reconnecting" ? "text-yellow-400" : "text-red-400";
  const rttWarning = stats.rtt !== null && stats.rtt > RTT_THRESHOLD;

  if (!showStats) {
    return (
      <>
        <button
          onClick={() => setShowStats(true)}
          className="absolute top-14 right-2 sm:right-4 z-20 live-overlay-btn px-2 py-1.5 flex items-center gap-1.5"
          data-testid="button-show-stats"
          title={stats.state}
        >
          <span className={`inline-block h-2 w-2 rounded-full ${dotColor} ${stats.state === "reconnecting" ? "animate-pulse" : ""}`} />
          <span className={`text-[10px] ${stateColor}`}>{stats.state}</span>
        </button>
        {showPerfSuggestion && (
          <div className="absolute top-24 right-2 sm:right-4 z-20 live-stats-panel p-3 text-xs text-white/90 min-w-[200px] max-w-[240px] space-y-2" data-testid="perf-suggestion-banner">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-yellow-400 shrink-0" />
              <span className="font-medium">{t("session.poorPerformance", { defaultValue: "Poor performance detected" })}</span>
            </div>
            <p className="text-white/60 text-[11px] leading-relaxed">{t("session.perfSuggestionDesc", { defaultValue: "High latency or frame drops detected. Enable performance mode to improve your stream." })}</p>
            <div className="flex gap-2">
              <button onClick={handleEnablePerfMode} className="flex-1 px-2 py-1 rounded bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30 text-[11px] font-medium" data-testid="button-enable-perf-mode">{t("session.enablePerfMode", { defaultValue: "Enable" })}</button>
              <button onClick={handleDismissPerfSuggestion} className="px-2 py-1 rounded bg-white/5 text-white/50 hover:bg-white/10 text-[11px]" data-testid="button-dismiss-perf-suggestion">{t("session.dismiss", { defaultValue: "Dismiss" })}</button>
            </div>
          </div>
        )}
      </>
    );
  }


  return (
    <>
      <div className="absolute top-14 right-2 sm:right-4 z-20 live-stats-panel p-3 text-xs text-white/80 space-y-1 min-w-[180px]" data-testid="connection-stats">
        <div className="flex items-center justify-between gap-4">
          <span className="font-semibold text-white/90">{t("session.streamStats")}</span>
          <button onClick={() => setShowStats(false)} className="text-white/40 text-[10px]">{t("session.hide")}</button>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${stats.state === "connected" ? "bg-green-400" : stats.state === "reconnecting" ? "bg-yellow-400" : "bg-red-400"}`} />
          <span className={stateColor} data-testid="text-connection-state">{stats.state}</span>
          {performanceMode && (
            <span className="ml-auto text-[10px] text-yellow-400 flex items-center gap-0.5" data-testid="text-perf-mode-indicator">
              <Zap className="h-2.5 w-2.5" />
              {t("session.perfMode", { defaultValue: "Perf" })}
            </span>
          )}
        </div>
        {stats.connectionTime !== null && (
          <div className="flex justify-between">
            <span className="text-white/50">{t("session.connectTime")}</span>
            <span data-testid="text-connect-time">{stats.connectionTime}ms</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-white/50">{t("session.rttLatency")}</span>
          <span className={rttWarning ? "text-yellow-400" : ""} data-testid="text-rtt">{stats.rtt !== null ? `${stats.rtt}ms` : "---"}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-white/50">{t("session.jitter")}</span>
          <span data-testid="text-jitter">{stats.jitter !== null ? `${stats.jitter}ms` : "---"}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-white/50">{t("session.packetsLost")}</span>
          <span data-testid="text-packets-lost">{stats.packetsLost !== null ? stats.packetsLost : "---"}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-white/50">{t("session.bitrate")}</span>
          <span data-testid="text-bitrate">{stats.bitrate !== null ? `${stats.bitrate} kbps` : "---"}</span>
        </div>
      </div>
      {showPerfSuggestion && (
        <div className="absolute top-[calc(14rem+8px)] right-2 sm:right-4 z-20 live-stats-panel p-3 text-xs text-white/90 min-w-[200px] max-w-[240px] space-y-2" data-testid="perf-suggestion-banner">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-yellow-400 shrink-0" />
            <span className="font-medium">{t("session.poorPerformance", { defaultValue: "Poor performance detected" })}</span>
          </div>
          <p className="text-white/60 text-[11px] leading-relaxed">{t("session.perfSuggestionDesc", { defaultValue: "High latency or frame drops detected. Enable performance mode to improve your stream." })}</p>
          <div className="flex gap-2">
            <button onClick={handleEnablePerfMode} className="flex-1 px-2 py-1 rounded bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30 text-[11px] font-medium" data-testid="button-enable-perf-mode">{t("session.enablePerfMode", { defaultValue: "Enable" })}</button>
            <button onClick={handleDismissPerfSuggestion} className="px-2 py-1 rounded bg-white/5 text-white/50 hover:bg-white/10 text-[11px]" data-testid="button-dismiss-perf-suggestion">{t("session.dismiss", { defaultValue: "Dismiss" })}</button>
          </div>
        </div>
      )}
    </>
  );
}

function ViewerPerfBanner() {
  const room = useRoomContext();
  const connectionState = useConnectionState();
  const { performanceMode, applyPerformanceMode } = usePerformanceMode();
  const { t } = useTranslation();
  const highRttCountRef = useRef(0);
  const prevFramesDroppedRef = useRef(0);
  const prevFramesReceivedRef = useRef(0);
  const dismissedRef = useRef(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (!room || connectionState !== ConnectionState.Connected) return;
    const interval = setInterval(async () => {
      try {
        let totalRtt = 0;
        let framesDropped = 0;
        let framesReceived = 0;
        let count = 0;
        for (const p of Array.from(room.remoteParticipants.values())) {
          for (const pub of Array.from(p.trackPublications.values())) {
            if (pub.track) {
              const report = await pub.track.getRTCStatsReport();
              if (report) {
                report.forEach((r: any) => {
                  if (r.type === "inbound-rtp" && r.kind === "video") {
                    if (r.framesDropped !== undefined) framesDropped += r.framesDropped;
                    if (r.framesReceived !== undefined) framesReceived += r.framesReceived;
                    count++;
                  }
                  if (r.type === "candidate-pair" && r.currentRoundTripTime !== undefined) {
                    totalRtt += r.currentRoundTripTime * 1000;
                  }
                });
              }
            }
          }
        }
        const rtt = totalRtt > 0 ? Math.round(totalRtt) : null;
        if (rtt !== null && rtt > 150) {
          highRttCountRef.current++;
        } else {
          highRttCountRef.current = Math.max(0, highRttCountRef.current - 1);
        }
        const deltaDropped = Math.max(0, framesDropped - prevFramesDroppedRef.current);
        const deltaReceived = Math.max(0, framesReceived - prevFramesReceivedRef.current);
        const deltaTotal = deltaDropped + deltaReceived;
        const dropRate = deltaTotal > 0 ? deltaDropped / deltaTotal : 0;
        prevFramesDroppedRef.current = framesDropped;
        prevFramesReceivedRef.current = framesReceived;
        const isPoor = highRttCountRef.current >= 3 || dropRate > 0.05;
        if (isPoor && !performanceMode && !dismissedRef.current) {
          setShowBanner(true);
        }
      } catch {}
    }, 10000);
    return () => clearInterval(interval);
  }, [room, connectionState, performanceMode]);

  if (!showBanner || performanceMode) return null;

  return (
    <div className="absolute top-14 right-2 sm:right-4 z-20 live-stats-panel p-3 text-xs text-white/90 min-w-[200px] max-w-[240px] space-y-2" data-testid="viewer-perf-banner">
      <div className="flex items-center gap-1.5">
        <AlertTriangle className="h-3.5 w-3.5 text-yellow-400 shrink-0" />
        <span className="font-medium">{t("session.poorPerformance", { defaultValue: "Poor connection detected" })}</span>
      </div>
      <p className="text-white/60 text-[11px] leading-relaxed">{t("session.viewerPerfDesc", { defaultValue: "Your connection quality is low. Enable performance mode for a smoother experience." })}</p>
      <div className="flex gap-2">
        <button
          onClick={() => { applyPerformanceMode(true); setShowBanner(false); }}
          className="flex-1 px-2 py-1 rounded bg-yellow-500/20 text-yellow-300 hover:bg-yellow-500/30 text-[11px] font-medium"
          data-testid="button-viewer-enable-perf"
        >
          {t("session.enablePerfMode", { defaultValue: "Enable" })}
        </button>
        <button
          onClick={() => { setShowBanner(false); dismissedRef.current = true; }}
          className="px-2 py-1 rounded bg-white/5 text-white/50 hover:bg-white/10 text-[11px]"
          data-testid="button-viewer-dismiss-perf"
        >
          {t("session.dismiss", { defaultValue: "Dismiss" })}
        </button>
      </div>
    </div>
  );
}

type BgMode = "none" | "blur" | "virtual";

const VIRTUAL_BG_PRESETS = [
  { id: "office", label: "Office", gradient: "linear-gradient(135deg, #1e293b 0%, #334155 100%)" },
  { id: "gradient-blue", label: "Blue", gradient: "linear-gradient(135deg, #0f172a 0%, #1e40af 50%, #3b82f6 100%)" },
  { id: "gradient-dark", label: "Dark", gradient: "linear-gradient(135deg, #0c0a09 0%, #1c1917 50%, #292524 100%)" },
];

function BackgroundEffectsMenu() {
  const room = useRoomContext();
  const [bgMode, setBgMode] = useState<BgMode>("none");
  const [bgPreset, setBgPreset] = useState<string>("office");
  const processorRef = useRef<any>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [supported, setSupported] = useState(true);
  const [applying, setApplying] = useState(false);
  const { performanceMode } = usePerformanceMode();

  useEffect(() => {
    import("@livekit/track-processors").then((mod) => {
      setSupported(mod.supportsBackgroundProcessors());
    }).catch(() => setSupported(false));
  }, []);

  useEffect(() => {
    if (performanceMode && bgMode !== "none") {
      const camPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      const camTrack = camPub?.track as any;
      if (camTrack && processorRef.current) {
        camTrack.stopProcessor().catch(() => {});
        processorRef.current = null;
      }
      setBgMode("none");
    }
  }, [performanceMode, room]);

  const generateVirtualBgImage = useCallback((gradient: string): string => {
    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }
    const canvas = canvasRef.current;
    canvas.width = 1920;
    canvas.height = 1080;
    const ctx = canvas.getContext("2d")!;

    const match = gradient.match(/linear-gradient\(([^,]+),\s*(.+)\)/);
    if (match) {
      const stops = match[2].split(/,\s*(?=#|rgba?|[a-z])/).map(s => s.trim());
      const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      stops.forEach((stop) => {
        const parts = stop.match(/(#[a-f0-9]+|rgba?\([^)]+\)|[a-z]+)\s*(\d+%)?/i);
        if (parts) {
          const color = parts[1];
          const pos = parts[2] ? parseInt(parts[2]) / 100 : undefined;
          if (pos !== undefined) grad.addColorStop(pos, color);
        }
      });
      ctx.fillStyle = grad;
    } else {
      ctx.fillStyle = "#1e293b";
    }
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  }, []);

  const applyEffect = useCallback(async (mode: BgMode, presetId?: string) => {
    if (applying) return;
    if (performanceMode && mode !== "none") return;
    setApplying(true);
    try {
      const camPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      const camTrack = camPub?.track as any;
      if (!camTrack) {
        setApplying(false);
        return;
      }

      if (mode === "none") {
        if (processorRef.current) {
          await camTrack.stopProcessor();
          processorRef.current = null;
        }
        setBgMode("none");
        setApplying(false);
        return;
      }

      const mod = await import("@livekit/track-processors");

      if (mode === "blur") {
        if (processorRef.current) {
          try {
            await processorRef.current.switchTo({ mode: "background-blur", blurRadius: 15 });
            setBgMode("blur");
            setApplying(false);
            return;
          } catch {
            await camTrack.stopProcessor().catch(() => {});
            processorRef.current = null;
          }
        }
        const processor = mod.BackgroundProcessor({ mode: "background-blur", blurRadius: 15 });
        await camTrack.setProcessor(processor);
        processorRef.current = processor;
        setBgMode("blur");
      } else if (mode === "virtual") {
        const preset = VIRTUAL_BG_PRESETS.find(p => p.id === (presetId || bgPreset)) || VIRTUAL_BG_PRESETS[0];
        const imagePath = generateVirtualBgImage(preset.gradient);
        if (processorRef.current) {
          try {
            await processorRef.current.switchTo({ mode: "virtual-background", imagePath });
            setBgMode("virtual");
            setBgPreset(preset.id);
            setApplying(false);
            return;
          } catch {
            await camTrack.stopProcessor().catch(() => {});
            processorRef.current = null;
          }
        }
        const processor = mod.BackgroundProcessor({ mode: "virtual-background", imagePath });
        await camTrack.setProcessor(processor);
        processorRef.current = processor;
        setBgMode("virtual");
        setBgPreset(preset.id);
      }
    } catch (err) {
      console.error("[Background] Failed to apply effect:", err);
    }
    setApplying(false);
  }, [room, bgPreset, applying, generateVirtualBgImage, performanceMode]);

  useEffect(() => {
    return () => {
      if (processorRef.current) {
        const camPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
        const camTrack = camPub?.track as any;
        if (camTrack) camTrack.stopProcessor().catch(() => {});
        processorRef.current = null;
      }
    };
  }, [room]);

  if (!supported) return null;

  const effectsDisabled = performanceMode;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`lk-button live-control-btn relative ${bgMode !== "none" ? "text-blue-400" : ""} ${effectsDisabled ? "opacity-50" : ""}`}
          data-testid="button-background-effects"
          title={effectsDisabled ? "Disabled in Performance Mode" : "Background effects"}
        >
          <UserCircle className="h-4 w-4" />
          {bgMode !== "none" && (
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-blue-500" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" side="top" className="glass-select-content text-white w-52 mb-2">
        {effectsDisabled && (
          <div className="px-2 py-1.5 text-[10px] text-yellow-400/80 flex items-center gap-1">
            <Zap className="h-3 w-3" />
            <span>Disabled in Performance Mode</span>
          </div>
        )}
        <DropdownMenuItem
          onClick={() => applyEffect("none")}
          className={`flex items-center gap-2 ${bgMode === "none" ? "text-blue-400" : ""}`}
          data-testid="bg-effect-none"
          disabled={applying}
        >
          {bgMode === "none" ? <Check className="h-3.5 w-3.5" /> : <span className="w-3.5" />}
          <span>No effect</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => applyEffect("blur")}
          className={`flex items-center gap-2 ${bgMode === "blur" ? "text-blue-400" : ""}`}
          data-testid="bg-effect-blur"
          disabled={applying || effectsDisabled}
        >
          {bgMode === "blur" ? <Check className="h-3.5 w-3.5" /> : <span className="w-3.5" />}
          <span>{applying ? "Applying..." : "Blur background"}</span>
        </DropdownMenuItem>
        <div className="px-2 py-1.5 text-[10px] text-muted-foreground uppercase tracking-wider">Virtual backgrounds</div>
        {VIRTUAL_BG_PRESETS.map((preset) => (
          <DropdownMenuItem
            key={preset.id}
            onClick={() => applyEffect("virtual", preset.id)}
            className={`flex items-center gap-2 ${bgMode === "virtual" && bgPreset === preset.id ? "text-blue-400" : ""}`}
            data-testid={`bg-effect-${preset.id}`}
            disabled={applying || effectsDisabled}
          >
            {bgMode === "virtual" && bgPreset === preset.id ? <Check className="h-3.5 w-3.5" /> : <span className="w-3.5" />}
            <span
              className="h-4 w-6 rounded-sm border border-white/10 shrink-0"
              style={{ background: preset.gradient }}
            />
            <span>{preset.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PerformanceModeApplier() {
  const { performanceMode } = usePerformanceMode();
  const room = useRoomContext();
  const prevModeRef = useRef(performanceMode);

  const applyPerfConstraints = useCallback(async () => {
    const camPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
    const camTrack = camPub?.track as any;
    if (!camTrack) return;

    if (performanceMode) {
      try { await camTrack.stopProcessor().catch(() => {}); } catch {}
      try {
        await camTrack.restartTrack({ width: 640, height: 360, frameRate: 24 });
      } catch {}
    } else {
      try {
        const res = isLargeScreen ? VideoPresets.h1080.resolution : VideoPresets.h720.resolution;
        await camTrack.restartTrack({ width: res.width, height: res.height, frameRate: res.frameRate });
      } catch {}
    }
  }, [performanceMode, room]);

  useEffect(() => {
    if (prevModeRef.current === performanceMode) return;
    prevModeRef.current = performanceMode;
    applyPerfConstraints();
  }, [performanceMode, applyPerfConstraints]);

  useEffect(() => {
    if (!performanceMode) return;

    const handleTrackPublished = (pub: any) => {
      if (pub?.source === Track.Source.Camera) {
        setTimeout(() => applyPerfConstraints(), 500);
      }
    };

    room.on(RoomEvent.LocalTrackPublished, handleTrackPublished);
    return () => {
      room.off(RoomEvent.LocalTrackPublished, handleTrackPublished);
    };
  }, [performanceMode, room, applyPerfConstraints]);

  return null;
}

function PerformanceModeToggle() {
  const { performanceMode, applyPerformanceMode, poorPerformanceDetected } = usePerformanceMode();
  const { t } = useTranslation();

  const handleToggle = useCallback(() => {
    applyPerformanceMode(!performanceMode);
  }, [performanceMode, applyPerformanceMode]);

  return (
    <button
      onClick={handleToggle}
      className={`lk-button live-control-btn relative ${performanceMode ? "text-yellow-400" : ""}`}
      data-testid="button-performance-mode"
      title={t("session.performanceMode", { defaultValue: "Performance Mode" })}
    >
      <Zap className="h-4 w-4" />
      {performanceMode && (
        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-yellow-500" />
      )}
      {poorPerformanceDetected && !performanceMode && (
        <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 animate-pulse" />
      )}
    </button>
  );
}

function StreamControls() {
  const { performanceMode } = usePerformanceMode();

  const [mirrored, setMirrored] = useState(() => {
    try { return localStorage.getItem("camera_mirrored") === "true"; } catch { return false; }
  });

  useEffect(() => {
    document.documentElement.classList.toggle("camera-mirrored", mirrored);
    return () => document.documentElement.classList.remove("camera-mirrored");
  }, [mirrored]);

  const toggleMirror = useCallback(() => {
    setMirrored(prev => {
      const next = !prev;
      try { localStorage.setItem("camera_mirrored", String(next)); } catch {}
      return next;
    });
  }, []);

  useEffect(() => {
    preWarmAudioContext();
  }, []);

  const screenShareCaptureOptions = useMemo(() => ({
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      sampleRate: 48000,
      channelCount: 2,
    },
    selfBrowserSurface: "include" as const,
    resolution: performanceMode
      ? { width: 1280, height: 720, frameRate: 15 }
      : { width: 1920, height: 1080, frameRate: 30 },
    contentHint: "motion" as const,
  }), [performanceMode]);


  return (
    <div className="absolute bottom-14 left-1/2 z-30 -translate-x-1/2" data-testid="stream-controls">
      <div className="lk-control-bar lk-compact live-control-bar relative flex items-center gap-1">
        <div className="flex items-center" data-testid="control-camera">
          <TrackToggle
            source={Track.Source.Camera}
            data-testid="button-toggle-camera"
            className="lk-button lk-button-toggle live-control-btn"
          />
          <MediaDeviceMenu
            kind="videoinput"
            data-testid="device-menu-camera"
          />
        </div>

        <div className="live-control-divider" />

        <div className="flex items-center" data-testid="control-mic">
          <TrackToggle
            source={Track.Source.Microphone}
            data-testid="button-toggle-mic"
            className="lk-button lk-button-toggle live-control-btn"
          />
          <MediaDeviceMenu
            kind="audioinput"
            data-testid="device-menu-mic"
          />
        </div>

        <div className="live-control-divider" />

        <TrackToggle
          source={Track.Source.ScreenShare}
          captureOptions={screenShareCaptureOptions}
          data-testid="button-toggle-screenshare"
          className="lk-button lk-button-toggle live-control-btn"
        />

        <div className="live-control-divider" />

        <PerformanceModeToggle />

        <div className="live-control-divider" />

        <BackgroundEffectsMenu />

        <div className="live-control-divider" />

        <button
          className={`lk-button live-control-btn relative ${mirrored ? "text-blue-400" : ""}`}
          onClick={toggleMirror}
          data-testid="button-mirror-camera"
          title="Mirror camera"
        >
          <FlipHorizontal2 className="h-4 w-4" />
          {mirrored && (
            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-blue-500" />
          )}
        </button>
      </div>
    </div>
  );
}

const isLargeScreen = typeof window !== "undefined" && window.innerWidth >= 1280;

const roomOptions: RoomOptions = {
  dynacast: true,
  adaptiveStream: { pixelDensity: "screen" },
  videoCaptureDefaults: {
    resolution: isLargeScreen
      ? VideoPresets.h1080.resolution
      : VideoPresets.h720.resolution,
  },
  publishDefaults: {
    simulcast: true,
    videoSimulcastLayers: [VideoPresets.h1080, VideoPresets.h540, VideoPresets.h216],
    videoCodec: "h264",
    dtx: true,
    red: true,
    screenShareEncoding: ScreenSharePresets.h1080fps30.encoding,
    screenShareSimulcastLayers: [ScreenSharePresets.h720fps30, ScreenSharePresets.h360fps15],
  },
  disconnectOnPageLeave: false,
  stopLocalTrackOnUnpublish: false,
  e2ee: undefined,
};

const VideoStage = memo(function VideoStage({ token, url, isOwner, canPublish, isCohost, onConnectionFailed, sessionId, isProvider, onScreenShareChange }: { token: string; url: string; isOwner: boolean; canPublish: boolean; isCohost: boolean; onConnectionFailed?: () => void; sessionId: string; isProvider: boolean; onScreenShareChange?: (active: boolean) => void }) {
  const [stableToken, setStableToken] = useState(token);
  const [stableUrl, setStableUrl] = useState(url);
  const latestTokenRef = useRef(token);
  const latestUrlRef = useRef(url);
  const [performanceMode, setPerformanceMode] = useState(false);
  const [poorPerformanceDetected, setPoorPerformanceDetected] = useState(false);
  const proxyFallbackRef = useRef(false);

  const perfContextValue = useMemo(() => ({
    performanceMode,
    setPerformanceMode,
    applyPerformanceMode: setPerformanceMode,
    poorPerformanceDetected,
    setPoorPerformanceDetected,
  }), [performanceMode, poorPerformanceDetected]);

  useEffect(() => {
    latestTokenRef.current = token;
    latestUrlRef.current = url;
  }, [token, url]);

  const reconnectPolicy = useRef({
    nextRetryDelayInMs: (context: { retryCount: number }) => {
      if (context.retryCount > 15) return null;
      const base = Math.min(1000 * Math.pow(1.5, Math.min(context.retryCount, 10)), 30000);
      const jitter = base * 0.3 * Math.random();
      return Math.round(base + jitter);
    },
  }).current;

  const shouldAutoPublish = isOwner || isCohost;
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const showUnmutePrompt = !canPublish && !audioUnlocked;

  const handleUnlockAudio = useCallback(() => {
    const ctx = new AudioContext();
    if (ctx.state === "suspended") {
      ctx.resume().then(() => ctx.close()).catch(() => ctx.close());
    } else {
      ctx.close();
    }
    const allAudio = document.querySelectorAll("audio, video");
    allAudio.forEach((el) => {
      const media = el as HTMLMediaElement;
      if (media.paused) {
        media.play().catch(() => {});
      }
    });
    setAudioUnlocked(true);
  }, []);

  return (
    <PerformanceModeContext.Provider value={perfContextValue}>
      <LiveKitRoom
        key={stableUrl}
        token={stableToken}
        serverUrl={stableUrl}
        connect={true}
        video={shouldAutoPublish}
        audio={shouldAutoPublish}
        options={{
          ...roomOptions,
          reconnectPolicy,
        }}
        className="h-full w-full"
        data-testid="livekit-room"
      >
        <RoomAudioRenderer />
        {showUnmutePrompt && (
          <button
            onClick={handleUnlockAudio}
            className="absolute top-4 right-4 z-30 flex items-center gap-2 live-overlay-btn px-3 py-2 text-sm text-white/90 animate-pulse"
            data-testid="button-unmute-audio"
          >
            <VolumeX className="h-4 w-4" />
            Tap to enable sound
          </button>
        )}
        {isProvider && <ConnectionStats />}
        {!isProvider && <ViewerPerfBanner />}
        {canPublish && <PerformanceModeApplier />}
        <NetworkRecoveryHandler sessionId={sessionId} latestTokenRef={latestTokenRef} latestUrlRef={latestUrlRef} setStableToken={setStableToken} setStableUrl={setStableUrl} />
        <ProxyFallbackDetector proxyFallbackRef={proxyFallbackRef} setStableUrl={setStableUrl} sessionId={sessionId} stableUrl={stableUrl} />
        <ConnectionReporter sessionId={sessionId} />
        <StageContent isOwner={isOwner} canPublish={canPublish} onConnectionFailed={onConnectionFailed} onScreenShareChange={onScreenShareChange} />
        {canPublish && <StreamControls />}
      </LiveKitRoom>
    </PerformanceModeContext.Provider>
  );
});

function getProxyUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/livekit-proxy`;
}

function reportConnectionEvent(sessionId: string, issueType: string, details: string) {
  try {
    fetch("/api/connection-report", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ sessionId, issueType, details }),
    }).catch(() => {});
  } catch {}
}

function ProxyFallbackDetector({ proxyFallbackRef, setStableUrl, sessionId, stableUrl }: {
  proxyFallbackRef: React.MutableRefObject<boolean>;
  setStableUrl: (u: string) => void;
  sessionId: string;
  stableUrl: string;
}) {
  const connectionState = useConnectionState();
  const disconnectedSinceRef = useRef<number | null>(null);
  const reportedRef = useRef(false);
  const isViaProxy = stableUrl.includes("/livekit-proxy");

  useEffect(() => {
    if (proxyFallbackRef.current) return;

    if (connectionState === ConnectionState.Connected) {
      disconnectedSinceRef.current = null;
      if (!reportedRef.current) {
        reportedRef.current = true;
        if (isViaProxy) {
          reportConnectionEvent(sessionId, "PROXY_CONNECTED", "Connected via proxy");
        } else {
          reportConnectionEvent(sessionId, "DIRECT_CONNECTED", "Connected to LiveKit directly");
          try { localStorage.removeItem("lk_needs_proxy"); } catch {}
        }
      }
      return;
    }

    if (disconnectedSinceRef.current === null) {
      disconnectedSinceRef.current = Date.now();
    }

    const elapsed = Date.now() - disconnectedSinceRef.current;
    const remaining = Math.max(0, 8000 - elapsed);

    const timer = setTimeout(() => {
      if (!proxyFallbackRef.current) {
        proxyFallbackRef.current = true;
        const proxyUrl = getProxyUrl();
        console.log("[LiveKit] Direct connection failed after 8s, switching to proxy:", proxyUrl);
        reportConnectionEvent(sessionId, "PROXY_ACTIVATED", "Switched to proxy after direct connection timeout");
        try { localStorage.setItem("lk_needs_proxy", "1"); } catch {}
        setStableUrl(proxyUrl);
      }
    }, remaining);
    return () => clearTimeout(timer);
  }, [connectionState, setStableUrl, proxyFallbackRef, sessionId, isViaProxy]);

  return null;
}

function ConnectionReporter({ sessionId }: { sessionId: string }) {
  const connectionState = useConnectionState();
  const prevStateRef = useRef(connectionState);
  const connectedAtRef = useRef<number | null>(null);

  useEffect(() => {
    const prev = prevStateRef.current;
    prevStateRef.current = connectionState;

    if (connectionState === ConnectionState.Connected && prev !== ConnectionState.Connected) {
      connectedAtRef.current = Date.now();
    }

    if (connectionState === ConnectionState.Disconnected && prev === ConnectionState.Connected) {
      const duration = connectedAtRef.current ? Math.round((Date.now() - connectedAtRef.current) / 1000) : 0;
      reportConnectionEvent(sessionId, "DISCONNECTED", `Was connected for ${duration}s`);
    }
  }, [connectionState, sessionId]);

  return null;
}

function NetworkRecoveryHandler({ sessionId, latestTokenRef, latestUrlRef, setStableToken, setStableUrl }: {
  sessionId: string;
  latestTokenRef: React.MutableRefObject<string>;
  latestUrlRef: React.MutableRefObject<string>;
  setStableToken: (t: string) => void;
  setStableUrl: (u: string) => void;
}) {
  const room = useRoomContext();
  const isRecoveringRef = useRef(false);
  const mountedRef = useRef(true);
  const lastRecoveryTriggerRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!room) return;

    const fetchFreshToken = async (): Promise<{ token: string; url: string } | null> => {
      try {
        const res = await fetch(`/api/sessions/${sessionId}/token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          if (data.token && data.url) {
            latestTokenRef.current = data.token;
            latestUrlRef.current = data.url;
            return { token: data.token, url: data.url };
          }
        }
      } catch (e) {
        console.warn("[LiveKit] Token fetch failed:", e);
      }
      return null;
    };

    const recoveryAttemptsRef = { total: 0, sinceLastSuccess: 0 };

    const reconnectWithFreshToken = async () => {
      if (isRecoveringRef.current || !mountedRef.current) return;
      if (room.state === ConnectionState.Connected || room.state === ConnectionState.Reconnecting) return;

      if (recoveryAttemptsRef.sinceLastSuccess >= 12) {
        console.log("[LiveKit] Max recovery attempts reached (12), stopping auto-reconnect");
        reportConnectionEvent(sessionId, "RECOVERY_EXHAUSTED", `Stopped after ${recoveryAttemptsRef.sinceLastSuccess} consecutive failed attempts`);
        isRecoveringRef.current = false;
        return;
      }

      isRecoveringRef.current = true;
      const proxyUrl = getProxyUrl();
      const maxAttempts = 8;

      for (let attempt = 0; attempt < maxAttempts && mountedRef.current; attempt++) {
        if (room.state === ConnectionState.Connected) {
          recoveryAttemptsRef.sinceLastSuccess = 0;
          break;
        }

        const delay = Math.min(2000 * Math.pow(1.8, Math.min(attempt, 8)), 45000);
        const jitter = delay * 0.2 * Math.random();
        await new Promise(r => setTimeout(r, Math.round(delay + jitter)));

        if (!mountedRef.current || room.state === ConnectionState.Connected) {
          if (room.state === ConnectionState.Connected) recoveryAttemptsRef.sinceLastSuccess = 0;
          break;
        }

        const fresh = await fetchFreshToken();
        if (!fresh || !mountedRef.current) continue;

        const useProxy = attempt >= 2;
        const connectUrl = useProxy ? proxyUrl : fresh.url;

        try {
          setStableToken(fresh.token);
          setStableUrl(connectUrl);
          await room.connect(connectUrl, fresh.token);
          console.log(`[LiveKit] Recovered connection ${useProxy ? "via proxy" : "direct"}`);
          reportConnectionEvent(sessionId, useProxy ? "PROXY_RECONNECTED" : "DIRECT_RECONNECTED", `Recovered on attempt ${attempt + 1}`);
          if (useProxy) { try { localStorage.setItem("lk_needs_proxy", "1"); } catch {} }
          recoveryAttemptsRef.sinceLastSuccess = 0;
          break;
        } catch (e) {
          recoveryAttemptsRef.total++;
          recoveryAttemptsRef.sinceLastSuccess++;
          console.warn(`[LiveKit] Recovery attempt ${attempt + 1}/${maxAttempts} failed (${useProxy ? "proxy" : "direct"}, total: ${recoveryAttemptsRef.sinceLastSuccess}):`, e);
          if (attempt === 1) {
            reportConnectionEvent(sessionId, "DIRECT_FAILED", `Direct connection failed after ${attempt + 1} attempts, switching to proxy`);
          }
        }
      }

      isRecoveringRef.current = false;
    };

    const screenShareTransitionRef = { active: false, timestamp: 0 };

    const handleDisconnect = (reason?: DisconnectReason) => {
      if (reason === DisconnectReason.CLIENT_INITIATED) return;
      console.log("[LiveKit] Disconnected, reason:", reason);

      const timeSinceScreenShareTransition = Date.now() - screenShareTransitionRef.timestamp;
      if (screenShareTransitionRef.active && timeSinceScreenShareTransition < 5000) {
        console.log("[LiveKit] Disconnect during screen share transition, recovering immediately");
        setTimeout(() => {
          if (room.state !== ConnectionState.Connected && room.state !== ConnectionState.Reconnecting) {
            reconnectWithFreshToken();
          }
        }, 500);
        return;
      }

      setTimeout(() => {
        if (room.state !== ConnectionState.Connected && room.state !== ConnectionState.Reconnecting) {
          reconnectWithFreshToken();
        }
      }, 5000);
    };

    const handleReconnecting = () => {
      console.log("[LiveKit] SDK reconnecting...");
    };

    const handleReconnected = () => {
      console.log("[LiveKit] Reconnected successfully");
      isRecoveringRef.current = false;
      screenShareTransitionRef.active = false;
    };

    const handleSignalResumed = () => {
      console.log("[LiveKit] Signal resumed");
      isRecoveringRef.current = false;
      screenShareTransitionRef.active = false;
    };

    const handleLocalTrackPublished = (_pub: any) => {
      if (_pub?.source === Track.Source.ScreenShare || _pub?.source === Track.Source.ScreenShareAudio) {
        console.log("[LiveKit] Screen share track published");
        screenShareTransitionRef.active = true;
        screenShareTransitionRef.timestamp = Date.now();
        setTimeout(() => {
          screenShareTransitionRef.active = false;
        }, 5000);
      }
    };

    const handleLocalTrackUnpublished = (_pub: any) => {
      if (_pub?.source === Track.Source.ScreenShare || _pub?.source === Track.Source.ScreenShareAudio) {
        console.log("[LiveKit] Screen share track unpublished");
        screenShareTransitionRef.active = true;
        screenShareTransitionRef.timestamp = Date.now();
        setTimeout(() => {
          screenShareTransitionRef.active = false;
        }, 5000);
      }
    };

    room.on(RoomEvent.Disconnected, handleDisconnect);
    room.on(RoomEvent.Reconnecting, handleReconnecting);
    room.on(RoomEvent.Reconnected, handleReconnected);
    room.on(RoomEvent.SignalResumed, handleSignalResumed);
    room.on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
    room.on(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);

    const RECOVERY_COOLDOWN = 30000;

    const triggerRecoveryIfNeeded = (source: string, delay: number) => {
      const now = Date.now();
      if (now - lastRecoveryTriggerRef.current < RECOVERY_COOLDOWN) return;
      if (recoveryAttemptsRef.sinceLastSuccess >= 12) return;
      lastRecoveryTriggerRef.current = now;
      setTimeout(() => {
        if (room.state === ConnectionState.Disconnected && !isRecoveringRef.current) {
          console.log(`[LiveKit] ${source}: triggering recovery`);
          reconnectWithFreshToken();
        }
      }, delay);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        triggerRecoveryIfNeeded("visibility-change", 2000);
      }
    };

    const handleOnline = () => {
      triggerRecoveryIfNeeded("online", 1500);
    };

    const handleFocus = () => {
      triggerRecoveryIfNeeded("focus", 3000);
    };

    const tokenRefreshInterval = setInterval(async () => {
      if (room.state === ConnectionState.Connected) {
        const fresh = await fetchFreshToken();
        if (fresh && mountedRef.current) {
          setStableToken(fresh.token);
          setStableUrl(fresh.url);
        }
      }
    }, 10 * 60 * 1000);

    const healthCheckInterval = setInterval(() => {
      if (room.state === ConnectionState.Disconnected && !isRecoveringRef.current) {
        console.log("[LiveKit] Health check: disconnected, triggering recovery");
        triggerRecoveryIfNeeded("health-check", 0);
      }
    }, 60000);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);
    window.addEventListener("focus", handleFocus);

    return () => {
      room.off(RoomEvent.Disconnected, handleDisconnect);
      room.off(RoomEvent.Reconnecting, handleReconnecting);
      room.off(RoomEvent.Reconnected, handleReconnected);
      room.off(RoomEvent.SignalResumed, handleSignalResumed);
      room.off(RoomEvent.LocalTrackPublished, handleLocalTrackPublished);
      room.off(RoomEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("focus", handleFocus);
      clearInterval(tokenRefreshInterval);
      clearInterval(healthCheckInterval);
    };
  }, [room, sessionId]);

  return null;
}

function StageContent({ isOwner, canPublish, onConnectionFailed, onScreenShareChange }: { isOwner: boolean; canPublish: boolean; onConnectionFailed?: () => void; onScreenShareChange?: (active: boolean) => void }) {
  const { t } = useTranslation();
  const connectionState = useConnectionState();
  const [hasConnected, setHasConnected] = useState(false);
  const [failedAt, setFailedAt] = useState<number | null>(null);

  useEffect(() => {
    if (connectionState === ConnectionState.Connected) {
      setHasConnected(true);
      setFailedAt(null);
    } else if (connectionState === ConnectionState.Disconnected && hasConnected) {
      setFailedAt((prev) => prev ?? Date.now());
    } else if (connectionState === ConnectionState.Reconnecting) {
      setFailedAt((prev) => prev ?? Date.now());
    }
  }, [connectionState, hasConnected]);

  const isFailedState = connectionState === ConnectionState.Disconnected || connectionState === ConnectionState.Reconnecting;
  const [timedOut, setTimedOut] = useState(false);
  const [showRetryBanner, setShowRetryBanner] = useState(false);

  useEffect(() => {
    if (!hasConnected) {
      const initialTimeout = setTimeout(() => {
        if (!hasConnected && connectionState !== ConnectionState.Connected) {
          setTimedOut(true);
        }
      }, 30000);
      return () => clearTimeout(initialTimeout);
    }
    if (failedAt === null) { setTimedOut(false); return; }
    const remaining = 90000 - (Date.now() - failedAt);
    if (remaining <= 0) { setTimedOut(true); return; }
    const timer = setTimeout(() => setTimedOut(true), remaining);
    return () => clearTimeout(timer);
  }, [failedAt, hasConnected, connectionState]);

  useEffect(() => {
    if (connectionState === ConnectionState.Connected) {
      setShowRetryBanner(false);
      return;
    }
    if (isFailedState && hasConnected) {
      const timer = setTimeout(() => setShowRetryBanner(true), 5000);
      return () => clearTimeout(timer);
    }
    if (!hasConnected && connectionState !== ConnectionState.Connected) {
      const timer = setTimeout(() => setShowRetryBanner(true), 15000);
      return () => clearTimeout(timer);
    }
  }, [connectionState, isFailedState, hasConnected]);

  const rawTracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: false },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
      { source: Track.Source.ScreenShareAudio, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  );

  const realTracks = rawTracks.filter(isTrackReference);

  const allScreenShares = realTracks.filter(
    (tr) => tr.source === Track.Source.ScreenShare && tr.publication?.track
  );
  const screenShareTrack = allScreenShares.length > 0 ? allScreenShares[0] : undefined;
  const cameraTracks = realTracks.filter(
    (tr) => tr.source === Track.Source.Camera && tr.publication?.track
  );

  useEffect(() => {
    onScreenShareChange?.(!!screenShareTrack);
    return () => { onScreenShareChange?.(false); };
  }, [!!screenShareTrack, onScreenShareChange]);

  if (timedOut && (isFailedState || (!hasConnected && connectionState !== ConnectionState.Connected))) {
    return (
      <div className="flex h-full w-full items-center justify-center live-stage">
        <div className="text-center max-w-sm px-4">
          <AlertCircle className="mx-auto mb-4 h-14 w-14 text-yellow-500/60" />
          <p className="text-lg font-medium text-white/70">
            {t("session.connectionFailed")}
          </p>
          <p className="mt-2 text-sm text-white/40 leading-relaxed">
            {hasConnected
              ? t("session.connectionFailedDesc")
              : t("session.connectionFailedInitial")}
          </p>
          <div className="mt-5 flex flex-col gap-2 items-center">
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => {
                setTimedOut(false);
                setFailedAt(null);
                setHasConnected(false);
                window.location.reload();
              }}
              className="border-white/20 text-white hover:bg-white/10 no-default-hover-elevate"
              data-testid="button-retry-connection"
            >
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
              {t("session.retryConnection")}
            </Button>
          </div>
          {!hasConnected && (
            <div className="mt-5 rounded-xl border border-yellow-500/20 bg-yellow-500/[0.05] p-4">
              <p className="text-xs text-yellow-200/70 leading-relaxed" data-testid="text-network-hint">
                {t("session.networkBlockedHint", { defaultValue: "Your internet connection may be too weak or unstable for live streaming. Try switching to a stronger Wi-Fi network, moving closer to your router, or using mobile data instead. If on mobile data, try moving to an area with better signal." })}
              </p>
            </div>
          )}
          <p className="mt-4 text-[11px] text-white/25 leading-relaxed">
            {t("session.chatStillWorks")}
          </p>
        </div>
      </div>
    );
  }

  const hasAnyTrack = cameraTracks.length > 0 || screenShareTrack;
  const isConnectedNoTracks = connectionState === ConnectionState.Connected && !hasAnyTrack && !isOwner;

  if (!hasAnyTrack) {
    return (
      <div className="flex h-full w-full items-center justify-center live-stage">
        <div className="text-center max-w-sm px-4">
          {isConnectedNoTracks ? (
            <>
              <div className="mx-auto mb-4 h-14 w-14 rounded-full border-2 border-green-500/40 flex items-center justify-center">
                <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
              </div>
              <p className="text-lg font-medium text-white/70" data-testid="text-connected-waiting">
                {t("session.connectedWaiting", { defaultValue: "You're connected" })}
              </p>
              <p className="mt-1 text-sm text-white/40">
                {t("session.hostVideoUnavailable", { defaultValue: "Waiting for the host to start broadcasting. The chat is available below." })}
              </p>
              <div className="mt-5">
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => window.location.reload()}
                  className="border-white/20 text-white hover:bg-white/10 no-default-hover-elevate"
                  data-testid="button-refresh-stream"
                >
                  <RefreshCw className="mr-2 h-3.5 w-3.5" />
                  {t("session.refreshStream", { defaultValue: "Refresh" })}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Loader2 className="mx-auto mb-4 h-12 w-12 text-white/30 animate-spin" />
              <p className="text-lg font-medium text-white/60">
                {isFailedState
                  ? t("session.connectingToServer")
                  : isOwner ? t("session.startingStream") : t("session.waitingForHost")}
              </p>
              <p className="mt-1 text-sm text-white/40">
                {isFailedState
                  ? t("session.connectingPleaseWait")
                  : isOwner ? t("session.cameraConnecting") : t("session.videoAppearShortly")}
              </p>
            </>
          )}
          {showRetryBanner && !isConnectedNoTracks && (
            <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-4">
              <p className="text-xs text-white/50 mb-3">
                {t("session.takingLonger", { defaultValue: "Taking longer than expected? Try reconnecting." })}
              </p>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => {
                  setShowRetryBanner(false);
                  setTimedOut(false);
                  setFailedAt(null);
                  setHasConnected(false);
                  window.location.reload();
                }}
                className="border-white/20 text-white hover:bg-white/10 no-default-hover-elevate"
                data-testid="button-retry-connection"
              >
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                {t("session.retryConnection")}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const retryOverlay = showRetryBanner && isFailedState ? (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 live-retry-banner px-4 py-2 flex items-center gap-3" data-testid="banner-retry-connection">
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-yellow-500 animate-pulse" />
        <span className="text-xs text-white/70">{t("session.connectionLost", { defaultValue: "Connection lost" })}</span>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setShowRetryBanner(false);
          setTimedOut(false);
          setFailedAt(null);
          setHasConnected(false);
          window.location.reload();
        }}
        className="h-6 px-3 text-[11px] rounded-full border-white/20 text-white hover:bg-white/10 no-default-hover-elevate"
        data-testid="button-retry-connection-overlay"
      >
        {t("session.retryConnection")}
      </Button>
    </div>
  ) : null;

  if (screenShareTrack) {
    return (
      <div className="relative h-full w-full live-stage live-stage-shadow live-screenshare-glow">
        {retryOverlay}
        <VideoTrack
          trackRef={screenShareTrack}
          className="h-full w-full object-contain relative z-[1]"
          data-testid="video-main-track"
        />
        {cameraTracks.length > 0 && (
          <div className="absolute bottom-4 right-4 flex gap-2 z-10">
            {cameraTracks.map((track, i) => (
              <div key={track.participant.sid + "-" + i} className="h-28 w-40 sm:h-32 sm:w-44 live-pip-frame" data-local-camera={track.participant.isLocal ? "true" : undefined}>
                <VideoTrack
                  trackRef={track}
                  className="h-full w-full object-cover"
                  data-testid={`video-pip-track-${i}`}
                />
                <div className="absolute bottom-1 left-1 live-name-tag">
                  {track.participant.name || track.participant.identity}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (cameraTracks.length === 1) {
    const isLocal = cameraTracks[0].participant.isLocal;
    return (
      <div className="relative h-full w-full live-stage live-stage-shadow">
        {retryOverlay}
        <div data-local-camera={isLocal ? "true" : undefined}>
          <VideoTrack
            trackRef={cameraTracks[0]}
            className="h-full w-full object-contain relative z-[1]"
            data-testid="video-main-track"
          />
        </div>
      </div>
    );
  }

  const gridClass = cameraTracks.length === 2
    ? "grid-cols-2"
    : cameraTracks.length <= 4
      ? "grid-cols-2 grid-rows-2"
      : "grid-cols-3 grid-rows-2";

  return (
    <div className={`relative h-full w-full live-stage grid ${gridClass} gap-1.5 p-1.5`} data-testid="video-grid">
      {retryOverlay}
      {cameraTracks.map((track, i) => (
        <div key={track.participant.sid + "-" + i} className={`relative live-grid-cell bg-black ${i === 0 ? "live-speaker-ring" : ""}`} data-local-camera={track.participant.isLocal ? "true" : undefined}>
          <VideoTrack
            trackRef={track}
            className="h-full w-full object-cover"
            data-testid={`video-grid-track-${i}`}
          />
          <div className="absolute bottom-2 left-2 live-name-tag flex items-center gap-1.5">
            {i === 0 && (
              <span className="flex items-center gap-0.5">
                <span className="live-waveform-bar" />
                <span className="live-waveform-bar" />
                <span className="live-waveform-bar" />
                <span className="live-waveform-bar" />
                <span className="live-waveform-bar" />
              </span>
            )}
            {track.participant.name || track.participant.identity}
          </div>
        </div>
      ))}
    </div>
  );
}

const CAPTION_LANGUAGES = [
  { code: "original", label: "Original" },
  { code: "en", label: "English" },
  { code: "es", label: "Espa\u00f1ol" },
  { code: "fr", label: "Fran\u00e7ais" },
  { code: "de", label: "Deutsch" },
  { code: "pt", label: "Portugu\u00eas" },
  { code: "it", label: "Italiano" },
  { code: "ja", label: "\u65e5\u672c\u8a9e" },
  { code: "ko", label: "\ud55c\uad6d\uc5b4" },
  { code: "zh", label: "\u4e2d\u6587" },
  { code: "hi", label: "\u0939\u093f\u0928\u094d\u0926\u0940" },
  { code: "ru", label: "\u0420\u0443\u0441\u0441\u043a\u0438\u0439" },
  { code: "ar", label: "\u0627\u0644\u0639\u0631\u0628\u064a\u0629" },
  { code: "no", label: "Norsk" },
  { code: "tl", label: "Tagalog" },
];

function useAudioCapture(
  wsRef: React.RefObject<WebSocket | null>,
  isActive: boolean,
  isOwner: boolean,
  userId?: string,
  onError?: (message: string) => void,
) {
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);

  useEffect(() => {
    if (!isActive || !isOwner) {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
      }
      if (workletNodeRef.current) {
        workletNodeRef.current.disconnect();
        workletNodeRef.current = null;
      }
      if (processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      return;
    }

    let cancelled = false;

    function setupWithScriptProcessor(stream: MediaStream, audioContext: AudioContext) {
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (e) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        const inputData = e.inputBuffer.getChannelData(0);
        const pcm16 = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        ws.send(pcm16.buffer);
      };

      source.connect(processor);
      processor.connect(audioContext.destination);
      console.log("[Captions] Using ScriptProcessor fallback for audio capture");
    }

    async function setupWithWorklet(stream: MediaStream, audioContext: AudioContext): Promise<boolean> {
      try {
        const workletCode = `
class PCM16Processor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    if (input && input.length > 0) {
      const channelData = input[0];
      if (channelData && channelData.length > 0) {
        const pcm16 = new Int16Array(channelData.length);
        for (let i = 0; i < channelData.length; i++) {
          const s = Math.max(-1, Math.min(1, channelData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        this.port.postMessage(pcm16.buffer, [pcm16.buffer]);
      }
    }
    return true;
  }
}
registerProcessor('pcm16-processor', PCM16Processor);`;

        const blob = new Blob([workletCode], { type: "application/javascript" });
        const workletUrl = URL.createObjectURL(blob);
        await audioContext.audioWorklet.addModule(workletUrl);
        URL.revokeObjectURL(workletUrl);

        const source = audioContext.createMediaStreamSource(stream);
        const workletNode = new AudioWorkletNode(audioContext, "pcm16-processor");
        workletNodeRef.current = workletNode;

        workletNode.port.onmessage = (e) => {
          const ws = wsRef.current;
          if (!ws || ws.readyState !== WebSocket.OPEN) return;
          ws.send(e.data);
        };

        source.connect(workletNode);
        workletNode.connect(audioContext.destination);
        console.log("[Captions] Using AudioWorklet for audio capture");
        return true;
      } catch (err) {
        console.warn("[Captions] AudioWorklet setup failed, will use fallback:", err);
        return false;
      }
    }

    function registerAsProvider() {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN && userId) {
        ws.send(JSON.stringify({ type: "caption:provider-ready" }));
      }
    }

    const providerRegistrationInterval = setInterval(registerAsProvider, 20000);

    async function startCapture() {
      try {
        registerAsProvider();

        const audioContext = new AudioContext({ sampleRate: 16000 });
        audioContextRef.current = audioContext;

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          audioContext.close();
          return;
        }
        mediaStreamRef.current = stream;

        const supportsWorklet = typeof audioContext.audioWorklet !== "undefined";
        if (supportsWorklet) {
          const success = await setupWithWorklet(stream, audioContext);
          if (!success) {
            setupWithScriptProcessor(stream, audioContext);
          }
        } else {
          setupWithScriptProcessor(stream, audioContext);
        }

        console.log("[Captions] Provider audio capture started");
      } catch (err) {
        console.error("[Captions] Audio capture error:", err);
        onError?.("Failed to access microphone for captions");
      }
    }

    startCapture();

    return () => {
      cancelled = true;
      clearInterval(providerRegistrationInterval);
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (workletNodeRef.current) {
        workletNodeRef.current.disconnect();
      }
      if (processorRef.current) {
        processorRef.current.disconnect();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [isActive, isOwner, userId]);
}

interface CaptionData {
  text: string;
  isFinal: boolean;
  language: string;
  timestamp: number;
}

function useCaptionState(
  wsRef: React.RefObject<WebSocket | null>,
  isOwner: boolean,
  sessionId: string,
  isLive: boolean,
  userId?: string,
) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [captionsActive, setCaptionsActive] = useState(false);
  const [currentCaption, setCurrentCaption] = useState("");
  const [finalCaptions, setFinalCaptions] = useState<string[]>([]);
  const [selectedLang, setSelectedLang] = useState("original");
  const [isTranslating, setIsTranslating] = useState(false);
  const [captionStale, setCaptionStale] = useState(false);
  const translateCacheRef = useRef<Map<string, string>>(new Map());
  const captionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastCaptionRef = useRef<number>(0);
  const staleCheckRef = useRef<NodeJS.Timeout | null>(null);

  const handleAudioError = useCallback((message: string) => {
    toast({ title: message, variant: "destructive" });
  }, [toast]);

  useAudioCapture(wsRef, captionsActive, isOwner, userId, handleAudioError);

  const { data: captionStatus } = useQuery<{ active: boolean; language: string | null }>({
    queryKey: ["/api/sessions", sessionId, "captions", "status"],
    enabled: isLive,
    refetchInterval: 10000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (captionStatus?.active && !captionsActive) {
      setCaptionsActive(true);
      if (lastCaptionRef.current === 0) {
        lastCaptionRef.current = Date.now();
      }
    }
  }, [captionStatus?.active]);

  const startCaptions = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/sessions/${sessionId}/captions/start`);
    },
    onSuccess: () => {
      setCaptionsActive(true);
      setCaptionStale(false);
      lastCaptionRef.current = Date.now();
    },
    onError: (error: Error) => {
      toast({
        title: error.message || t("session.captionError"),
        variant: "destructive",
      });
    },
  });

  const toggleCaptions = useCallback((enabled: boolean) => {
    setCaptionsEnabled(enabled);
    if (enabled && !captionsActive) {
      startCaptions.mutate();
    }
    if (!enabled) {
      setCurrentCaption("");
      setFinalCaptions([]);
    }
  }, [captionsActive, startCaptions]);

  const translateText = useCallback(async (text: string, lang: string, sourceLang?: string): Promise<string> => {
    if (lang === "original" || !text.trim()) return text;
    if (sourceLang && sourceLang === lang) return text;
    const cacheKey = `${sourceLang || "auto"}:${lang}:${text}`;
    const cached = translateCacheRef.current.get(cacheKey);
    if (cached) return cached;

    try {
      setIsTranslating(true);
      const res = await fetch("/api/translate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text, targetLang: lang, sourceLang }),
      });
      const data = await res.json();
      const result = data.translatedText || text;
      translateCacheRef.current.set(cacheKey, result);
      if (translateCacheRef.current.size > 500) {
        const keys = Array.from(translateCacheRef.current.keys());
        for (let i = 0; i < 100; i++) {
          translateCacheRef.current.delete(keys[i]);
        }
      }
      return result;
    } catch {
      return text;
    } finally {
      setIsTranslating(false);
    }
  }, []);

  useEffect(() => {
    if (captionsActive && captionsEnabled) {
      staleCheckRef.current = setInterval(() => {
        if (lastCaptionRef.current > 0 && Date.now() - lastCaptionRef.current > 15000) {
          setCaptionStale(true);
        }
      }, 10000);
    } else {
      if (staleCheckRef.current) {
        clearInterval(staleCheckRef.current);
        staleCheckRef.current = null;
      }
      setCaptionStale(false);
    }
    return () => {
      if (staleCheckRef.current) {
        clearInterval(staleCheckRef.current);
        staleCheckRef.current = null;
      }
    };
  }, [captionsActive, captionsEnabled]);

  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;

    const handleMessage = async (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "caption:update" && captionsEnabled) {
          const caption: CaptionData = msg.data;
          lastCaptionRef.current = Date.now();
          setCaptionStale(false);

          if (captionTimeoutRef.current) {
            clearTimeout(captionTimeoutRef.current);
          }

          let displayText = caption.text;
          if (selectedLang !== "original") {
            displayText = await translateText(caption.text, selectedLang, caption.language);
          }

          if (caption.isFinal) {
            setFinalCaptions((prev) => {
              const next = [...prev, displayText];
              return next.slice(-3);
            });
            setCurrentCaption("");
          } else {
            setCurrentCaption(displayText);
          }

          captionTimeoutRef.current = setTimeout(() => {
            setCurrentCaption("");
            setFinalCaptions([]);
          }, 8000);
        } else if (msg.type === "captions:started") {
          setCaptionsActive(true);
          if (lastCaptionRef.current === 0) {
            lastCaptionRef.current = Date.now();
          }
        } else if (msg.type === "captions:stopped") {
          setCaptionsActive(false);
          setCurrentCaption("");
          setFinalCaptions([]);
        } else if (msg.type === "caption:error") {
          toast({
            title: msg.data?.message || "Transcription error",
            variant: "destructive",
          });
        }
      } catch {}
    };

    ws.addEventListener("message", handleMessage);
    return () => ws.removeEventListener("message", handleMessage);
  }, [captionsEnabled, selectedLang, translateText, toast]);

  const hasCaption = currentCaption || finalCaptions.length > 0;
  const selectedLangLabel = CAPTION_LANGUAGES.find((l) => l.code === selectedLang)?.label || "Original";

  return {
    captionsEnabled,
    setCaptionsEnabled,
    toggleCaptions,
    captionsActive,
    currentCaption,
    finalCaptions,
    selectedLang,
    setSelectedLang,
    selectedLangLabel,
    isTranslating,
    hasCaption,
    captionStale,
  };
}

const FloatingChat = memo(function FloatingChat({
  messages,
  chatMessage,
  setChatMessage,
  sendMessage,
  sendReaction,
  deleteMessage,
  permissions,
  isLive,
  isAuthenticated,
  sessionId,
  t,
}: {
  messages: (Message & { userName: string })[];
  chatMessage: string;
  setChatMessage: (v: string) => void;
  sendMessage: { mutate: () => void; isPending: boolean };
  sendReaction: { mutate: (type: string) => void };
  deleteMessage: { mutate: (id: string) => void };
  permissions: SessionState["permissions"];
  isLive: boolean;
  isAuthenticated: boolean;
  sessionId: string;
  t: (key: string, defaultValue?: string) => string;
}) {
  const [minimized, setMinimized] = useState(() => {
    try { return localStorage.getItem("floating_chat_minimized") === "true"; } catch { return false; }
  });
  const [position, setPosition] = useState(() => {
    try {
      const saved = localStorage.getItem("floating_chat_position");
      if (saved) {
        const parsed = JSON.parse(saved);
        const r = Number(parsed.right);
        const b = Number(parsed.bottom);
        if (!isNaN(r) && !isNaN(b)) {
          const maxRight = window.innerWidth - 340;
          const maxBottom = window.innerHeight - 440;
          return {
            right: Math.max(0, Math.min(r, maxRight)),
            bottom: Math.max(0, Math.min(b, maxBottom)),
          };
        }
      }
    } catch {}
    const defaultBottom = Math.max(80, Math.min(window.innerHeight * 0.3, window.innerHeight - 500));
    return { right: 16, bottom: defaultBottom };
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const lastSeenCountRef = useRef(messages.length);
  const initializedRef = useRef(false);
  const dragRef = useRef<{ startX: number; startY: number; startRight: number; startBottom: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const floatingChatEndRef = useRef<HTMLDivElement>(null);
  const floatingIsNearBottomRef = useRef(true);
  const [floatingNewMessages, setFloatingNewMessages] = useState(0);

  useEffect(() => {
    if (!initializedRef.current) {
      lastSeenCountRef.current = messages.length;
      initializedRef.current = true;
      return;
    }
    if (minimized) {
      const newMessages = messages.length - lastSeenCountRef.current;
      if (newMessages > 0) setUnreadCount(prev => prev + newMessages);
      lastSeenCountRef.current = messages.length;
    } else {
      if (floatingIsNearBottomRef.current) {
        lastSeenCountRef.current = messages.length;
        setUnreadCount(0);
        setFloatingNewMessages(0);
      } else {
        const newCount = messages.length - lastSeenCountRef.current;
        if (newCount > 0) setFloatingNewMessages(prev => prev + newCount);
        lastSeenCountRef.current = messages.length;
      }
    }
  }, [messages.length, minimized]);

  useEffect(() => {
    if (!minimized && floatingIsNearBottomRef.current) {
      floatingChatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setFloatingNewMessages(0);
    }
  }, [messages.length, minimized]);

  const scrollFloatingToBottom = useCallback(() => {
    floatingChatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    floatingIsNearBottomRef.current = true;
    setFloatingNewMessages(0);
  }, []);

  const toggleMinimized = useCallback(() => {
    setMinimized(prev => {
      const next = !prev;
      try { localStorage.setItem("floating_chat_minimized", String(next)); } catch {}
      if (!next) {
        setUnreadCount(0);
        setFloatingNewMessages(0);
        floatingIsNearBottomRef.current = true;
        lastSeenCountRef.current = messages.length;
        setTimeout(() => {
          floatingChatEndRef.current?.scrollIntoView({ behavior: "auto" });
        }, 50);
      }
      return next;
    });
  }, [messages.length]);

  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => {
        const panelW = panelRef.current?.offsetWidth || 320;
        const panelH = panelRef.current?.offsetHeight || 420;
        const maxRight = Math.max(0, window.innerWidth - panelW);
        const maxBottom = Math.max(0, window.innerHeight - panelH);
        const clamped = {
          right: Math.max(0, Math.min(prev.right, maxRight)),
          bottom: Math.max(0, Math.min(prev.bottom, maxBottom)),
        };
        if (clamped.right === prev.right && clamped.bottom === prev.bottom) return prev;
        return clamped;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    dragRef.current = {
      startX: clientX,
      startY: clientY,
      startRight: position.right,
      startBottom: position.bottom,
    };

    const handleMove = (ev: MouseEvent | TouchEvent) => {
      if (!dragRef.current) return;
      const cx = "touches" in ev ? ev.touches[0].clientX : ev.clientX;
      const cy = "touches" in ev ? ev.touches[0].clientY : ev.clientY;
      const dx = dragRef.current.startX - cx;
      const dy = dragRef.current.startY - cy;
      const panelW = panelRef.current?.offsetWidth || 320;
      const panelH = panelRef.current?.offsetHeight || 420;
      const newRight = Math.max(0, Math.min(window.innerWidth - panelW, dragRef.current.startRight + dx));
      const newBottom = Math.max(0, Math.min(window.innerHeight - panelH, dragRef.current.startBottom + dy));
      setPosition({ right: newRight, bottom: newBottom });
    };

    const handleUp = () => {
      dragRef.current = null;
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
      document.removeEventListener("touchmove", handleMove);
      document.removeEventListener("touchend", handleUp);
      setPosition(pos => {
        try { localStorage.setItem("floating_chat_position", JSON.stringify(pos)); } catch {}
        return pos;
      });
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    document.addEventListener("touchmove", handleMove);
    document.addEventListener("touchend", handleUp);
  }, [position]);

  if (minimized) {
    return (
      <div
        className="floating-chat-minimized"
        style={{ right: `${position.right}px`, bottom: `${position.bottom}px` }}
        data-testid="floating-chat-minimized"
      >
        <Button
          size="icon"
          className="h-12 w-12 rounded-full bg-primary/90 hover:bg-primary text-white shadow-lg"
          onClick={toggleMinimized}
          data-testid="button-floating-chat-expand"
        >
          <MessageSquare className="h-5 w-5" />
        </Button>
        {unreadCount > 0 && (
          <span
            className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] text-white font-bold shadow-md"
            data-testid="badge-floating-chat-unread"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      className="floating-chat-panel"
      style={{ right: `${position.right}px`, bottom: `${position.bottom}px` }}
      data-testid="floating-chat-panel"
    >
      <div
        className="floating-chat-header"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        data-testid="floating-chat-drag-handle"
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5 text-primary/70" />
          <span className="text-xs font-medium text-white/90">{t("session.liveChat")}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 text-white/60 hover:text-white no-default-hover-elevate"
            onClick={toggleMinimized}
            data-testid="button-floating-chat-minimize"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="relative">
        <ScrollArea
          className="floating-chat-messages"
          onScrollCapture={(e: React.UIEvent<HTMLDivElement>) => {
            const el = e.currentTarget.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
            if (el) {
              const threshold = 60;
              floatingIsNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
              if (floatingIsNearBottomRef.current) setFloatingNewMessages(0);
            }
          }}
        >
          <div className="space-y-2 p-2">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className="group flex items-start gap-1.5"
                data-testid={`floating-chat-message-${msg.id}`}
              >
                <Avatar className="h-5 w-5 shrink-0">
                  <AvatarFallback className="text-[8px] bg-white/10 text-white/70">
                    {msg.userName?.[0]?.toUpperCase() || "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-medium text-white/80 truncate">{msg.userName}</span>
                    <span className="text-[8px] text-white/40">
                      {format(new Date(msg.createdAt), "h:mm a")}
                    </span>
                  </div>
                  <p className="text-[11px] text-white/60 break-words leading-snug">{msg.text}</p>
                </div>
                {permissions.canModerate && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="invisible h-4 w-4 shrink-0 group-hover:visible text-white/40 hover:text-red-400 no-default-hover-elevate"
                    onClick={() => deleteMessage.mutate(msg.id)}
                    data-testid={`button-floating-delete-msg-${msg.id}`}
                  >
                    <Trash2 className="h-2.5 w-2.5" />
                  </Button>
                )}
              </div>
            ))}
            <div ref={floatingChatEndRef} />
          </div>
        </ScrollArea>
        {floatingNewMessages > 0 && (
          <button
            onClick={scrollFloatingToBottom}
            className="absolute bottom-1 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/90 hover:bg-primary text-white text-[10px] font-medium shadow-lg transition-colors"
            data-testid="button-floating-new-messages"
          >
            <ArrowDown className="h-3 w-3" />
            {floatingNewMessages} new {floatingNewMessages === 1 ? "message" : "messages"}
          </button>
        )}
      </div>

      {permissions.canReact && isLive && (
        <div className="flex items-center justify-center gap-0.5 border-t border-white/[0.08] px-2 py-1">
          {REACTION_TYPES.map((r) => (
            <Button
              key={r.type}
              size="icon"
              variant="ghost"
              className="h-6 w-6 no-default-hover-elevate"
              onClick={() => sendReaction.mutate(r.type)}
              data-testid={`button-floating-reaction-${r.type}`}
            >
              <r.icon className={`h-3 w-3 ${r.color}`} />
            </Button>
          ))}
        </div>
      )}

      {permissions.canChat && isLive && (
        <div className="border-t border-white/[0.08] p-2">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage.mutate();
            }}
            className="flex items-center gap-1.5"
          >
            <Input
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              placeholder={t("session.sendAMessage")}
              className="flex-1 h-7 text-[11px] glass-input border-white/10"
              maxLength={500}
              data-testid="input-floating-chat"
            />
            <Button
              type="submit"
              size="icon"
              className="h-7 w-7 shrink-0"
              disabled={!chatMessage.trim() || sendMessage.isPending}
              data-testid="button-floating-send-chat"
            >
              <Send className="h-3 w-3" />
            </Button>
          </form>
        </div>
      )}

      {!isAuthenticated && isLive && (
        <div className="border-t border-white/[0.08] p-2 text-center">
          <p className="text-[10px] text-white/40 mb-1">{t("session.signInToChat", "Sign in to chat")}</p>
          <Link href={`/login?redirect=/live/${sessionId}`}>
            <Button size="sm" className="h-6 text-[10px] btn-gradient-capsule" data-testid="button-floating-login">
              <LogIn className="mr-1 h-2.5 w-2.5" />
              {t("session.logIn", "Log in")}
            </Button>
          </Link>
        </div>
      )}

      {!isLive && (
        <div className="border-t border-white/[0.08] p-2 text-center">
          <p className="text-[10px] text-white/40">{t("session.chatDisabled")}</p>
        </div>
      )}
    </div>
  );
});

function CaptionOverlay({
  caption,
  chatCollapsed = false,
}: {
  caption: ReturnType<typeof useCaptionState>;
  chatCollapsed?: boolean;
}) {
  const { t } = useTranslation();
  const { captionsEnabled, hasCaption, finalCaptions, currentCaption, isTranslating } = caption;

  if (!captionsEnabled || !hasCaption) return null;

  return (
    <div
      className={`absolute left-1/2 z-20 -translate-x-1/2 pointer-events-none ${
        chatCollapsed
          ? 'bottom-24 sm:bottom-28 w-[85%] sm:max-w-[70%]'
          : 'bottom-12 sm:bottom-24 w-[90%] sm:max-w-[80%]'
      }`}
      data-testid="caption-overlay"
    >
      <div className="live-caption-bubble px-3 sm:px-4 py-1.5 sm:py-2 max-h-[30vh] overflow-y-auto">
        {finalCaptions.map((text, i) => (
          <p key={i} className="text-center text-xs sm:text-sm text-white/70 leading-snug sm:leading-relaxed">
            {text}
          </p>
        ))}
        {currentCaption && (
          <p className="text-center text-xs sm:text-sm text-white leading-snug sm:leading-relaxed italic" data-testid="text-current-caption">
            {currentCaption}
          </p>
        )}
        {isTranslating && (
          <p className="text-center text-[9px] sm:text-[10px] text-white/40 mt-0.5 sm:mt-1">{t("session.translating")}</p>
        )}
      </div>
    </div>
  );
}

function CaptionToolbar({
  caption,
  isLive,
}: {
  caption: ReturnType<typeof useCaptionState>;
  isOwner?: boolean;
  isLive: boolean;
}) {
  const { t } = useTranslation();
  const {
    captionsEnabled,
    toggleCaptions,
    captionStale,
    selectedLang,
    setSelectedLang,
    selectedLangLabel,
  } = caption;

  if (!isLive) return null;

  return (
    <div
      className="live-caption-toolbar flex items-center justify-between gap-2 px-3 py-1.5"
      data-testid="caption-toolbar"
    >
      <div className="flex items-center gap-2">
        <Subtitles className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">{t("session.liveCaptions")}</span>
        {captionsEnabled && captionStale && (
          <span className="flex items-center gap-1 text-yellow-500" data-testid="caption-stale-indicator">
            <AlertCircle className="h-3 w-3" />
            <span className="text-[10px] hidden sm:inline">No signal</span>
          </span>
        )}
      </div>

      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant={captionsEnabled ? "secondary" : "outline"}
          onClick={() => toggleCaptions(!captionsEnabled)}
          className={`h-7 text-xs ${captionsEnabled ? "ring-1 ring-blue-400/40" : ""}`}
          data-testid="button-toggle-captions-viewer"
        >
          <Subtitles className={`h-3 w-3 ${captionsEnabled ? "text-blue-400" : ""}`} />
          <span className="ml-1 hidden sm:inline">{captionsEnabled ? t("session.hideSubtitles") : t("session.showSubtitles")}</span>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="gap-1 h-7 text-xs"
              data-testid="button-caption-language"
            >
              <Languages className="h-3 w-3" />
              <span className="hidden sm:inline">{selectedLangLabel}</span>
              <ChevronDown className="h-2.5 w-2.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-60 overflow-y-auto glass-select-content text-white">
            {CAPTION_LANGUAGES.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => setSelectedLang(lang.code)}
                className={selectedLang === lang.code ? "bg-accent" : ""}
                data-testid={`caption-lang-${lang.code}`}
              >
                {lang.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function CoHostPanel({ sessionId, isOwner, cohosts }: { sessionId: string; isOwner: boolean; cohosts: CohostInfo[] }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [showPanel, setShowPanel] = useState(false);

  const { data: searchResults } = useQuery<{ id: string; name: string; email: string; displayName: string | null }[]>({
    queryKey: ["/api/providers/search", `q=${searchQuery}`],
    queryFn: async () => {
      if (searchQuery.length < 2) return [];
      const res = await fetch(`/api/providers/search?q=${encodeURIComponent(searchQuery)}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: searchQuery.length >= 2,
  });

  const addCohost = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("POST", `/api/sessions/${sessionId}/cohosts`, { userId });
    },
    onSuccess: () => {
      toast({ title: t("session.cohostAdded") });
      setSearchQuery("");
      // Delay refetch so it doesn't race with the LiveKit connection
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"] });
      }, 1500);
    },
    onError: (err: any) => {
      toast({ title: t("session.cohostError"), description: err.message, variant: "destructive" });
    },
  });

  const removeCohost = useMutation({
    mutationFn: async (userId: string) => {
      await apiRequest("DELETE", `/api/sessions/${sessionId}/cohosts/${userId}`);
    },
    onSuccess: () => {
      toast({ title: t("session.cohostRemoved") });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"] });
      }, 1500);
    },
  });

  if (!isOwner) return null;

  const existingUserIds = new Set(cohosts.map((c) => c.userId));
  const filteredResults = (searchResults || []).filter((r) => !existingUserIds.has(r.id));

  return (
    <div className="relative" data-testid="cohost-panel">
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowPanel(!showPanel)}
        className="gap-1.5 live-overlay-btn no-default-hover-elevate"
        data-testid="button-toggle-cohost-panel"
      >
        <Users className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{t("session.cohosts")}</span>
        {cohosts.length > 0 && (
          <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-[10px]">
            {cohosts.length}
          </Badge>
        )}
      </Button>

      {showPanel && (
        <div className="absolute top-full right-0 mt-2 w-72 sm:w-80 live-cohost-dropdown z-50 p-3" data-testid="cohost-panel-dropdown">
          <h4 className="text-sm font-medium text-white mb-2">{t("session.manageCohosts")}</h4>

          <div className="mb-3">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("session.searchEducators")}
              className="h-8 text-sm bg-white/5 border-white/10 text-white placeholder:text-white/40"
              data-testid="input-search-cohost"
            />
            {filteredResults.length > 0 && (
              <div className="mt-1 max-h-32 overflow-y-auto rounded border border-white/10 bg-black/80">
                {filteredResults.map((provider) => (
                  <button
                    key={provider.id}
                    onClick={() => addCohost.mutate(provider.id)}
                    disabled={addCohost.isPending}
                    className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-white/10 transition-colors"
                    data-testid={`button-add-cohost-${provider.id}`}
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-[10px] bg-white/10 text-white">
                        {(provider.displayName || provider.name)?.[0]?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-white truncate">{provider.displayName || provider.name}</p>
                      <p className="text-[10px] text-white/40 truncate">{provider.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {cohosts.length > 0 ? (
            <div className="space-y-1.5">
              {cohosts.map((cohost) => (
                <div key={cohost.userId} className="flex items-center gap-2 rounded px-2 py-1.5 bg-white/5" data-testid={`cohost-item-${cohost.userId}`}>
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="text-[10px] bg-white/10 text-white">
                      {cohost.userName?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-white truncate">{cohost.userName}</p>
                    <Badge variant="outline" className="text-[9px] px-1 py-0 border-white/20 text-white/60">
                      {cohost.status === "ACCEPTED" ? t("session.cohostLive") : t("session.cohostInvited")}
                    </Badge>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-white/40 hover:text-red-400 hover:bg-red-400/10"
                    onClick={() => removeCohost.mutate(cohost.userId)}
                    data-testid={`button-remove-cohost-${cohost.userId}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-white/40 text-center py-2">{t("session.noCohosts")}</p>
          )}
        </div>
      )}
    </div>
  );
}

function StreamPlaceholder({ isLive, message }: { isLive: boolean; message?: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full w-full items-center justify-center live-stage">
      <div className="text-center">
        {isLive ? (
          <>
            <AlertCircle className="mx-auto mb-4 h-16 w-16 text-yellow-500/50" />
            <p className="text-lg font-medium text-white/60">{t("session.streamNotAvailable")}</p>
            <p className="mt-1 max-w-sm text-sm text-white/40">
              {message || t("session.livekitNotConfigured")}
            </p>
          </>
        ) : (
          <>
            <Video className="mx-auto mb-4 h-16 w-16 text-white/30" />
            <p className="text-lg font-medium text-white/60">{t("session.streamOffline")}</p>
            <p className="mt-1 text-sm text-white/40">{t("session.sessionHasEnded")}</p>
          </>
        )}
      </div>
    </div>
  );
}

export default function LiveSession() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const [chatMessage, setChatMessage] = useState("");
  const [localMessages, setLocalMessages] = useState<(Message & { userName: string })[]>([]);
  const [floatingReactions, setFloatingReactions] = useState<{ id: number; type: string; x: number }[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const [mainChatNewMessages, setMainChatNewMessages] = useState(0);
  const reactionIdRef = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);
  const [sessionIsLive, setSessionIsLive] = useState(false);
  const [isSessionOwner, setIsSessionOwner] = useState(false);
  const stageHasMountedRef = useRef(false);
  const sessionWasLiveRef = useRef(false);
  const lockedTokenRef = useRef<{ token: string; url: string } | null>(null);
  const [chatCollapsed, setChatCollapsed] = useState(() => {
    try { return localStorage.getItem("chat_collapsed") === "true"; } catch { return false; }
  });
  const [chatWidth, setChatWidth] = useState(() => {
    try { return parseInt(localStorage.getItem("chat_width") || "340", 10); } catch { return 340; }
  });
  const isResizing = useRef(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(340);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [isLandscapeMobile, setIsLandscapeMobile] = useState(false);
  const [participants, setParticipants] = useState<{ userId: string; displayName: string; role: string }[]>([]);
  const [showParticipants, setShowParticipants] = useState(false);
  const [wasKicked, setWasKicked] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    const STORAGE_KEY = "ps_build_version";
    const checkVersion = async () => {
      try {
        const res = await fetch("/api/build-version", { cache: "no-store" });
        if (!res.ok) return;
        const { version } = await res.json();
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && stored !== version) {
          localStorage.setItem(STORAGE_KEY, version);
          window.location.reload();
          return;
        }
        localStorage.setItem(STORAGE_KEY, version);
      } catch {}
    };
    checkVersion();
    const interval = setInterval(checkVersion, 300000);
    return () => clearInterval(interval);
  }, []);

  const chatBeforeImmersive = useRef<boolean | null>(null);

  useEffect(() => {
    const desktopMq = window.matchMedia("(min-width: 1024px)");
    const landscapeMq = window.matchMedia("(orientation: landscape) and (max-height: 500px)");
    const desktopHandler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    const landscapeHandler = (e: MediaQueryListEvent) => {
      setIsLandscapeMobile(e.matches);
      if (e.matches) {
        if (chatBeforeImmersive.current === null) chatBeforeImmersive.current = chatCollapsed;
        setChatCollapsed(true);
      } else if (chatBeforeImmersive.current !== null && !isFullscreen) {
        setChatCollapsed(chatBeforeImmersive.current);
        chatBeforeImmersive.current = null;
      }
    };
    desktopMq.addEventListener("change", desktopHandler);
    landscapeMq.addEventListener("change", landscapeHandler);
    setIsDesktop(desktopMq.matches);
    if (landscapeMq.matches) {
      setIsLandscapeMobile(true);
      if (chatBeforeImmersive.current === null) chatBeforeImmersive.current = chatCollapsed;
      setChatCollapsed(true);
    }
    return () => {
      desktopMq.removeEventListener("change", desktopHandler);
      landscapeMq.removeEventListener("change", landscapeHandler);
    };
  }, []);

  useEffect(() => {
    const nav = document.querySelector("header.glass-nav") as HTMLElement | null;
    if (nav) {
      nav.style.display = (isLandscapeMobile || isFullscreen) ? "none" : "";
    }
  }, [isLandscapeMobile, isFullscreen]);

  useEffect(() => {
    const fsHandler = () => {
      const fs = !!(document.fullscreenElement || (document as any).webkitFullscreenElement);
      setIsFullscreen(fs);
    };
    document.addEventListener("fullscreenchange", fsHandler);
    document.addEventListener("webkitfullscreenchange", fsHandler);
    return () => {
      document.removeEventListener("fullscreenchange", fsHandler);
      document.removeEventListener("webkitfullscreenchange", fsHandler);
      const navEl = document.querySelector("header.glass-nav") as HTMLElement | null;
      if (navEl) navEl.style.display = "";
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const doc = document as any;
    if (!document.fullscreenElement && !doc.webkitFullscreenElement) {
      if (el.requestFullscreen) {
        el.requestFullscreen().catch(() => {});
      } else if ((el as any).webkitRequestFullscreen) {
        (el as any).webkitRequestFullscreen();
      } else {
        const video = el.querySelector("video");
        if (video && (video as any).webkitEnterFullscreen) {
          (video as any).webkitEnterFullscreen();
        }
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if (doc.webkitExitFullscreen) {
        doc.webkitExitFullscreen();
      }
    }
  }, []);

  const handleChatCollapse = useCallback((collapsed: boolean) => {
    setChatCollapsed(collapsed);
    try { localStorage.setItem("chat_collapsed", String(collapsed)); } catch {}
  }, []);

  const handleScreenShareChange = useCallback((active: boolean) => {
    setIsScreenSharing(active);
    if (active) {
      document.documentElement.classList.add("perf-mode");
    } else {
      document.documentElement.classList.remove("perf-mode");
    }
  }, []);

  const chatWidthRef = useRef(chatWidth);
  useEffect(() => { chatWidthRef.current = chatWidth; }, [chatWidth]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = chatWidthRef.current;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      const delta = resizeStartX.current - ev.clientX;
      const newWidth = Math.min(600, Math.max(280, resizeStartWidth.current + delta));
      chatWidthRef.current = newWidth;
      const chatEl = document.querySelector('[data-chat-panel]') as HTMLElement;
      if (chatEl) chatEl.style.width = `${newWidth}px`;
    };
    const handleMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      setChatWidth(chatWidthRef.current);
      try { localStorage.setItem("chat_width", String(chatWidthRef.current)); } catch {}
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  const caption = useCaptionState(wsRef, isSessionOwner, sessionId || "", sessionIsLive, user?.id);

  const { data, isLoading, isError } = useQuery<SessionState>({
    queryKey: ["/api/sessions", sessionId, "state"],
    refetchInterval: (query) => {
      if (query.state.data?.session?.status === "ENDED") return false;
      if (query.state.error) return false;
      return 30000;
    },
    retry: 2,
    refetchOnWindowFocus: false,
    structuralSharing: true,
  });

  const [livekitNotConfigured, setLivekitNotConfigured] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const forceProxy = urlParams.get("proxy") === "1";
  const localProxyPref = (() => { try { return localStorage.getItem("lk_needs_proxy") === "1"; } catch { return false; } })();
  const requestProxy = forceProxy || localProxyPref;

  const { data: tokenData, error: tokenError, isLoading: tokenLoading } = useQuery<LiveKitToken>({
    queryKey: ["/api/sessions", sessionId, "token", requestProxy ? "proxy" : "direct"],
    queryFn: async () => {
      const tokenUrl = requestProxy
        ? `/api/sessions/${sessionId}/token?proxy=1`
        : `/api/sessions/${sessionId}/token`;
      const res = await fetch(tokenUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (res.status === 503) {
        setLivekitNotConfigured(true);
        throw new Error("LiveKit not configured");
      }
      if (!res.ok) {
        throw new Error("Failed to get streaming token");
      }
      return res.json();
    },
    enabled: data?.session?.status === "LIVE",
    retry: (failureCount, error) => {
      if (error.message === "LiveKit not configured") return false;
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * Math.pow(2, attemptIndex), 5000),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: true,
  });

  // Lock the token the moment we get it — never let it go undefined mid-session
  useEffect(() => {
    if (tokenData?.token && tokenData?.url) {
      lockedTokenRef.current = { token: tokenData.token, url: tokenData.url };
    }
    if (data && data.session?.status === "LIVE" && tokenData?.token && tokenData?.url) {
      stageHasMountedRef.current = true;
      sessionWasLiveRef.current = true;
    }
  }, [tokenData, data]);

  // Use locked token so VideoStage never gets null props from query refetches mid-session
  const activeToken = lockedTokenRef.current ?? (tokenData?.token && tokenData?.url ? { token: tokenData.token, url: tokenData.url } : null);

  useEffect(() => {
    if (data) {
      if (data.messages) setLocalMessages(data.messages);
      setSessionIsLive(data.session.status === "LIVE");
      setIsSessionOwner(data.permissions.isOwner);
    }
  }, [data?.messages, data?.session?.status, data?.permissions?.isOwner]);

  useEffect(() => {
    if (!sessionId || !user) return;

    let ws: WebSocket | null = null;
    let retryCount = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let pingTimer: ReturnType<typeof setInterval> | null = null;
    let intentionalClose = false;
    let lastPongTime = Date.now();

    const connect = () => {
      if (intentionalClose) return;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      ws = new WebSocket(`${protocol}//${window.location.host}/ws?sessionId=${sessionId}`);
      wsRef.current = ws;

      ws.onopen = () => {
        retryCount = 0;
        lastPongTime = Date.now();
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          if (ws?.readyState === WebSocket.OPEN) {
            if (Date.now() - lastPongTime > 60000) {
              ws.close();
              return;
            }
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 15000);
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "participants:request" }));
        }
      };

      ws.onmessage = (event) => {
        try {
          if (typeof event.data !== "string") return;
          const msg = JSON.parse(event.data);
          if (msg.type === "pong") {
            lastPongTime = Date.now();
            return;
          }
          if (msg.type === "message:new") {
            setLocalMessages((prev) => [...prev, msg.data]);
          } else if (msg.type === "message:delete") {
            setLocalMessages((prev) =>
              prev.map((m) => (m.id === msg.data.messageId ? { ...m, deletedAt: new Date().toISOString() as any } : m))
            );
          } else if (msg.type === "reaction:new") {
            addFloatingReaction(msg.data.type);
          } else if (msg.type === "session:ended") {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
              mediaRecorderRef.current.stop();
            }
            mediaRecorderRef.current = null;
            queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"], refetchType: "none" });
            setTimeout(() => {
              queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"] });
            }, 2000);
          } else if (msg.type === "cohost:added" || msg.type === "cohost:removed" || msg.type === "cohost:joined") {
            queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"], refetchType: "none" });
            setTimeout(() => {
              queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"] });
            }, 1500);
          } else if (msg.type === "participants:list") {
            setParticipants(msg.data || []);
          } else if (msg.type === "participant:join") {
            setParticipants((prev) => {
              if (prev.some((p) => p.userId === msg.data.userId)) return prev;
              return [...prev, msg.data];
            });
          } else if (msg.type === "participant:leave") {
            setParticipants((prev) => prev.filter((p) => p.userId !== msg.data.userId));
          } else if (msg.type === "participant:kicked") {
            setParticipants((prev) => prev.filter((p) => p.userId !== msg.data.userId));
            if (user && msg.data.userId === user.id) {
              setWasKicked(true);
              intentionalClose = true;
              ws?.close();
            }
          } else if (msg.type === "participant:muted") {
            if (user && msg.data.userId === user.id) {
              toast({ title: t("session.youWereMuted", "You have been muted by a moderator"), variant: "destructive" });
            }
          }
        } catch {}
      };

      ws.onclose = () => {
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = null;
        wsRef.current = null;
        if (!intentionalClose && retryCount < 50) {
          const delay = retryCount < 3 ? 500 : Math.min(1000 * Math.pow(1.3, retryCount - 3), 15000);
          retryCount++;
          retryTimer = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        ws?.close();
      };
    };

    connect();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && (!ws || ws.readyState !== WebSocket.OPEN)) {
        retryCount = 0;
        if (retryTimer) clearTimeout(retryTimer);
        connect();
      }
    };

    const handleOnline = () => {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        retryCount = 0;
        if (retryTimer) clearTimeout(retryTimer);
        connect();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    return () => {
      intentionalClose = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (pingTimer) clearInterval(pingTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      ws?.close();
    };
  }, [sessionId, user]);

  const mainChatPrevLenRef = useRef(localMessages.length);
  useEffect(() => {
    const diff = localMessages.length - mainChatPrevLenRef.current;
    mainChatPrevLenRef.current = localMessages.length;
    if (isNearBottomRef.current) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setMainChatNewMessages(0);
    } else if (diff > 0) {
      setMainChatNewMessages(prev => prev + diff);
    }
  }, [localMessages]);

  const scrollMainChatToBottom = useCallback(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    isNearBottomRef.current = true;
    setMainChatNewMessages(0);
  }, []);

  const addFloatingReaction = useCallback((type: string) => {
    const id = reactionIdRef.current++;
    const x = 10 + Math.random() * 80;
    setFloatingReactions((prev) => [...prev, { id, type, x }]);
    setTimeout(() => {
      setFloatingReactions((prev) => prev.filter((r) => r.id !== id));
    }, 2000);
  }, []);

  const sendMessage = useMutation({
    mutationFn: async () => {
      if (!chatMessage.trim()) return;
      await apiRequest("POST", `/api/sessions/${sessionId}/messages`, { text: chatMessage.trim() });
      setChatMessage("");
    },
    onError: (err: any) => {
      const msg = err.message || "";
      if (msg.includes("restricted word")) {
        toast({ title: t("session.wordRestricted", "Message blocked"), description: msg, variant: "destructive" });
      } else {
        toast({ title: "Error", description: msg, variant: "destructive" });
      }
    },
  });

  const sendReaction = useMutation({
    mutationFn: async (type: string) => {
      await apiRequest("POST", `/api/sessions/${sessionId}/reactions`, { type });
      addFloatingReaction(type);
    },
  });

  const deleteMessage = useMutation({
    mutationFn: async (messageId: string) => {
      await apiRequest("DELETE", `/api/sessions/${sessionId}/messages/${messageId}`);
    },
  });

  const kickParticipant = useMutation({
    mutationFn: async (targetUserId: string) => {
      await apiRequest("POST", `/api/sessions/${sessionId}/participants/${targetUserId}/kick`);
    },
    onSuccess: () => {
      toast({ title: t("session.participantKicked", "Participant removed from session") });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const muteParticipant = useMutation({
    mutationFn: async (targetUserId: string) => {
      await apiRequest("POST", `/api/sessions/${sessionId}/participants/${targetUserId}/mute`);
    },
    onSuccess: () => {
      toast({ title: t("session.participantMuted", "Participant muted") });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const endSession = useMutation({
    mutationFn: async () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        await new Promise<void>((resolve) => {
          const recorder = mediaRecorderRef.current!;
          const origOnStop = recorder.onstop;
          recorder.onstop = (ev) => {
            if (origOnStop) origOnStop.call(recorder, ev);
            resolve();
          };
          recorder.stop();
        });

        const maxWait = 60000;
        const start = Date.now();
        while (activeRecordingIdRef.current && Date.now() - start < maxWait) {
          await new Promise((r) => setTimeout(r, 500));
        }
      }
      await apiRequest("POST", `/api/sessions/${sessionId}/end`);
    },
    onSuccess: () => {
      queryClient.setQueryData<SessionState>(["/api/sessions", sessionId, "state"], (old) => {
        if (!old) return old;
        return { ...old, session: { ...old.session, status: "ENDED" as any } };
      });
      toast({ title: t("session.sessionEnded") });
    },
  });

  const acceptInvite = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      await apiRequest("POST", `/api/sessions/${sessionId}/cohosts/${user.id}/accept`);
    },
    onSuccess: () => {
      toast({ title: t("session.cohostAccepted") });
      queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"], refetchType: "none" });
      // Trigger a background refetch after a safe delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"] });
      }, 2000);
    },
  });

  const declineInvite = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");
      await apiRequest("DELETE", `/api/sessions/${sessionId}/cohosts/${user.id}`);
    },
    onSuccess: () => {
      toast({ title: t("session.cohostDeclined") });
      queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"], refetchType: "none" });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"] });
      }, 2000);
    },
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const activeRecordingIdRef = useRef<string | null>(null);
  const lastRecordingBlobRef = useRef<Blob | null>(null);
  const failedAssemblyRecordingIdRef = useRef<string | null>(null);
  const recordingStartTimeRef = useRef<number>(0);
  const chunkUploadIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chunkIndexRef = useRef<number>(0);
  const lastChunkUploadedRef = useRef<number>(0);
  const chunkFlushInProgressRef = useRef(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isActivelyRecording, setIsActivelyRecording] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "retrying" | "success" | "failed">("idle");
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [chunkUploadMessage, setChunkUploadMessage] = useState<string>("");

  useEffect(() => {
    if (!isUploading && !endSession.isPending && !isActivelyRecording) return;
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = isUploading
        ? "Recording is still being saved. Are you sure you want to leave?"
        : "Recording is in progress. Leaving may cause data loss.";

      if (isActivelyRecording && activeRecordingIdRef.current) {
        const currentChunks = recordingChunksRef.current;
        if (currentChunks.length > 0) {
          const combinedBlob = new Blob(currentChunks, { type: currentChunks[0]?.type || "video/webm" });
          if (combinedBlob.size > 0) {
            const chunkIdx = chunkIndexRef.current;
            const formData = new FormData();
            formData.append("recordingId", activeRecordingIdRef.current);
            formData.append("chunkIndex", String(chunkIdx));
            formData.append("chunk", combinedBlob);
            const beaconSent = navigator.sendBeacon(`/api/recordings/${activeRecordingIdRef.current}/beacon-chunk`, formData);
            if (!beaconSent) {
              try {
                const xhr = new XMLHttpRequest();
                xhr.open("POST", `/api/recordings/${activeRecordingIdRef.current}/beacon-chunk`, false);
                xhr.send(formData);
                console.log(`[ChunkUpload] Sync XHR flush sent for chunk ${chunkIdx}`);
              } catch (xhrErr) {
                console.warn(`[ChunkUpload] Sync XHR fallback failed for chunk ${chunkIdx}:`, xhrErr);
              }
            } else {
              console.log(`[ChunkUpload] Beacon flush sent for chunk ${chunkIdx} (${(combinedBlob.size / 1024).toFixed(0)}KB)`);
            }
          }
        }
      }
    };
    window.addEventListener("beforeunload", beforeUnloadHandler);
    return () => {
      window.removeEventListener("beforeunload", beforeUnloadHandler);
    };
  }, [isUploading, endSession.isPending, isActivelyRecording]);

  const uploadChunkToServer = useCallback(async (chunkBlob: Blob, recordingId: string, chunkIdx: number, maxRetries = 3) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (attempt > 0) {
        const delay = Math.min(2000 * Math.pow(2, attempt - 1), 10000);
        console.log(`[ChunkUpload] Retry ${attempt}/${maxRetries} for chunk ${chunkIdx} in ${delay}ms`);
        await new Promise(r => setTimeout(r, delay));
      }
      try {
        const urlRes = await fetch(`/api/recordings/${recordingId}/upload-chunk`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ chunkIndex: chunkIdx }),
        });
        if (!urlRes.ok) continue;
        const { uploadURL, objectPath } = await urlRes.json();

        const putRes = await fetch(uploadURL, {
          method: "PUT",
          body: chunkBlob,
          headers: { "Content-Type": chunkBlob.type || "video/webm" },
        });
        if (!putRes.ok) continue;

        const confirmRes = await fetch(`/api/recordings/${recordingId}/confirm-chunk`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ chunkIndex: chunkIdx, objectPath }),
        });
        if (!confirmRes.ok) continue;

        return true;
      } catch (err) {
        console.warn(`[ChunkUpload] Attempt ${attempt + 1}/${maxRetries} failed for chunk ${chunkIdx}:`, err);
      }
    }
    console.error(`[ChunkUpload] All ${maxRetries} attempts exhausted for chunk ${chunkIdx}`);
    return false;
  }, []);

  const flushChunksToServer = useCallback(async () => {
    if (chunkFlushInProgressRef.current) return;
    const recordingId = activeRecordingIdRef.current;
    if (!recordingId) return;
    const currentChunks = recordingChunksRef.current;
    if (currentChunks.length === 0) return;

    chunkFlushInProgressRef.current = true;
    try {
      const snapshot = [...currentChunks];
      const snapshotEnd = currentChunks.length;
      const combinedBlob = new Blob(snapshot, { type: snapshot[0]?.type || "video/webm" });
      if (combinedBlob.size === 0) return;

      const chunkIdx = chunkIndexRef.current;
      chunkIndexRef.current++;

      setChunkUploadMessage(`Uploading chunk ${chunkIdx + 1}...`);
      const ok = await uploadChunkToServer(combinedBlob, recordingId, chunkIdx);
      if (ok) {
        const liveChunks = recordingChunksRef.current;
        const newChunksSinceSnapshot = liveChunks.slice(snapshotEnd);
        recordingChunksRef.current = newChunksSinceSnapshot;
        lastChunkUploadedRef.current = 0;
        setChunkUploadMessage(`Chunk ${chunkIdx + 1} saved`);
        setTimeout(() => setChunkUploadMessage(""), 3000);
        console.log(`[ChunkUpload] Chunk ${chunkIdx} uploaded (${(combinedBlob.size / 1024).toFixed(0)}KB), freed ${snapshot.length} blobs, kept ${newChunksSinceSnapshot.length} new blobs`);
      } else {
        setChunkUploadMessage(`Chunk ${chunkIdx + 1} failed — will retry`);
        setTimeout(() => setChunkUploadMessage(""), 5000);
        console.warn(`[ChunkUpload] Chunk ${chunkIdx} failed — data kept for next cycle`);
      }
    } finally {
      chunkFlushInProgressRef.current = false;
    }
  }, [uploadChunkToServer]);

  useEffect(() => {
    if (!isActivelyRecording) return;
    const visibilityHandler = () => {
      if (document.visibilityState === "hidden" && isActivelyRecording) {
        flushChunksToServer();
      }
    };
    document.addEventListener("visibilitychange", visibilityHandler);
    return () => {
      document.removeEventListener("visibilitychange", visibilityHandler);
    };
  }, [isActivelyRecording, flushChunksToServer]);

  const uploadWithRetry = useCallback(async (blob: Blob, recordingId: string, maxAttempts = 3): Promise<boolean> => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (attempt > 0) {
        const delays = [2000, 4000, 8000];
        const delay = delays[attempt - 1] || 8000;
        setUploadStatus("retrying");
        toast({ title: `Retrying upload (attempt ${attempt + 1}/${maxAttempts})...` });
        await new Promise((r) => setTimeout(r, delay));
      }

      try {
        setUploadProgress(10);
        const urlRes = await fetch(`/api/recordings/${recordingId}/request-upload-url`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ name: `recording-${recordingId}.webm`, size: blob.size, contentType: blob.type || "video/webm" }),
        });
        if (!urlRes.ok) continue;
        const { uploadURL, objectPath } = await urlRes.json();

        setUploadProgress(30);
        const putRes = await fetch(uploadURL, {
          method: "PUT",
          body: blob,
          headers: { "Content-Type": blob.type || "video/webm" },
        });
        if (!putRes.ok) continue;

        setUploadProgress(80);
        const confirmRes = await fetch(`/api/recordings/${recordingId}/confirm-upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ objectPath }),
        });
        if (confirmRes.ok) {
          setUploadProgress(100);
          return true;
        }
      } catch (err) {
        console.warn(`Upload attempt ${attempt + 1} failed:`, err);
      }
    }
    return false;
  }, [toast]);

  const uploadRecordingBlob = useCallback(async (rawBlob: Blob, recordingId: string) => {
    setIsUploading(true);
    setUploadStatus("uploading");
    setUploadProgress(0);
    activeRecordingIdRef.current = recordingId;
    lastRecordingBlobRef.current = rawBlob;
    let didFail = false;
    try {
      let blob = rawBlob;
      const duration = recordingStartTimeRef.current > 0 ? Date.now() - recordingStartTimeRef.current : 0;
      if (duration > 0) {
        try {
          const { default: fixWebmDuration } = await import("fix-webm-duration");
          blob = await fixWebmDuration(rawBlob, duration, { logger: false });
          console.log(`Fixed WebM duration: ${Math.round(duration / 1000)}s, size: ${rawBlob.size} → ${blob.size}`);
        } catch (fixErr) {
          console.warn("Could not fix WebM duration, using raw blob:", fixErr);
        }
      }

      const success = await uploadWithRetry(blob, recordingId);

      if (success) {
        setUploadStatus("success");
        toast({ title: "Recording saved successfully" });
        queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"], refetchType: "none" });
        queryClient.invalidateQueries({ queryKey: ["/api/recordings"] });
      } else {
        didFail = true;
        setUploadStatus("failed");
        toast({ title: "Upload failed", description: "All retry attempts exhausted. Click Retry or try from your dashboard.", variant: "destructive" });
        await fetch(`/api/recordings/${recordingId}/confirm-upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ failed: true }),
        }).catch(() => {});
      }
    } catch (err: any) {
      didFail = true;
      setUploadStatus("failed");
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
      await fetch(`/api/recordings/${recordingId}/confirm-upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ failed: true }),
      }).catch(() => {});
    } finally {
      setIsUploading(false);
      if (!didFail) {
        activeRecordingIdRef.current = null;
        lastRecordingBlobRef.current = null;
      }
      setTimeout(() => {
        setUploadProgress(0);
      }, 5000);
    }
  }, [toast, sessionId, uploadWithRetry]);

  const gatherMediaTracks = useCallback(async () => {
    const audioCtx = new AudioContext();
    const audioDestination = audioCtx.createMediaStreamDestination();
    let audioSourceCount = 0;
    let videoTrack: MediaStreamTrack | null = null;
    let screenShareTrack: MediaStreamTrack | null = null;
    const capturedTrackIds = new Set<string>();

    const allVideoEls = document.querySelectorAll("video");
    allVideoEls.forEach((el) => {
      const videoEl = el as HTMLVideoElement;
      try {
        if (videoEl.srcObject) {
          const vidStream = videoEl.srcObject as MediaStream;
          const vTracks = vidStream.getVideoTracks();
          vTracks.forEach((vt) => {
            if (vt.readyState === "live") {
              const settings = vt.getSettings();
              const isScreenShare = (settings.width && settings.width >= 1280) ||
                vt.label?.toLowerCase().includes("screen") ||
                vt.label?.toLowerCase().includes("window") ||
                vt.label?.toLowerCase().includes("tab");
              if (isScreenShare && !screenShareTrack) {
                screenShareTrack = vt;
              } else if (!videoTrack) {
                videoTrack = vt;
              }
            }
          });
          vidStream.getAudioTracks().forEach((t) => {
            if (t.readyState === "live" && !capturedTrackIds.has(t.id)) {
              capturedTrackIds.add(t.id);
              const source = audioCtx.createMediaStreamSource(new MediaStream([t]));
              source.connect(audioDestination);
              audioSourceCount++;
            }
          });
        }
      } catch (e) {
        console.warn("Could not capture video element:", e);
      }
    });

    if (screenShareTrack) {
      videoTrack = screenShareTrack;
    }

    const allAudioEls = document.querySelectorAll("audio");
    allAudioEls.forEach((el) => {
      const audioEl = el as HTMLAudioElement;
      try {
        if (audioEl.srcObject) {
          const remoteStream = audioEl.srcObject as MediaStream;
          remoteStream.getAudioTracks().forEach((t) => {
            if (t.readyState === "live" && !capturedTrackIds.has(t.id)) {
              capturedTrackIds.add(t.id);
              const source = audioCtx.createMediaStreamSource(new MediaStream([t]));
              source.connect(audioDestination);
              audioSourceCount++;
            }
          });
        }
      } catch (e) {
        console.warn("Could not capture remote audio:", e);
      }
    });

    if (!videoTrack && audioSourceCount === 0) {
      try {
        const localStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true }).catch(() =>
          navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => null)
        );
        if (localStream) {
          const vTracks = localStream.getVideoTracks();
          if (vTracks.length > 0 && vTracks[0].readyState === "live") {
            videoTrack = vTracks[0];
          }
          localStream.getAudioTracks().forEach((t) => {
            if (t.readyState === "live") {
              const source = audioCtx.createMediaStreamSource(new MediaStream([t]));
              source.connect(audioDestination);
              audioSourceCount++;
            }
          });
        }
      } catch (e) {
        console.warn("getUserMedia fallback failed:", e);
      }
    }

    const finalTracks: MediaStreamTrack[] = [];
    if (videoTrack) {
      finalTracks.push(videoTrack.clone());
    }
    audioDestination.stream.getAudioTracks().forEach((t) => {
      finalTracks.push(t);
    });

    return { audioCtx, audioDestination, audioSourceCount, videoTrack, finalTracks };
  }, []);

  const startClientRecording = useCallback(async () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      return;
    }
    try {
      recordingChunksRef.current = [];

      let gathered = await gatherMediaTracks();

      if (gathered.finalTracks.length === 0) {
        console.log("[Recording] No tracks found on first attempt, polling for up to 60s...");
        gathered.audioCtx.close().catch(() => {});
        for (let attempt = 1; attempt <= 30; attempt++) {
          await new Promise((r) => setTimeout(r, 2000));
          if (!activeRecordingIdRef.current) {
            console.log("[Recording] Recording cancelled during track polling");
            return;
          }
          gathered = await gatherMediaTracks();
          if (gathered.finalTracks.length > 0) {
            console.log(`[Recording] Found tracks on attempt ${attempt + 1}`);
            break;
          }
          gathered.audioCtx.close().catch(() => {});
        }
      }

      const { audioCtx, audioSourceCount, finalTracks } = gathered;
      audioContextRef.current = audioCtx;

      if (finalTracks.length === 0) {
        console.warn("[Recording] No media tracks available after 60s polling — leaving recording in RECORDING state for retry");
        toast({ title: "Recording waiting for tracks", description: "No audio or video tracks found yet. Recording will retry when tracks become available.", variant: "destructive" });
        audioCtx.close().catch(() => {});
        audioContextRef.current = null;
        autoRecordTriggeredRef.current = false;
        return;
      }

      const stream = new MediaStream(finalTracks);
      recordingStreamRef.current = stream;

      const hasVideo = stream.getVideoTracks().length > 0;
      const mimeTypes = hasVideo
        ? ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"]
        : ["audio/webm;codecs=opus", "audio/webm"];
      let selectedMime = "";
      for (const mime of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mime)) {
          selectedMime = mime;
          break;
        }
      }
      if (!selectedMime) selectedMime = hasVideo ? "video/webm" : "audio/webm";

      const recorder = new MediaRecorder(stream, {
        mimeType: selectedMime,
        videoBitsPerSecond: 2500000,
      });
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordingChunksRef.current.push(e.data);
      };
      recorder.onerror = (event: Event) => {
        console.error("[MediaRecorder] Error event:", event);
        toast({ title: "Recording error", description: "The recorder encountered an error. Saving what was captured...", variant: "destructive" });
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          try { mediaRecorderRef.current.stop(); } catch {}
        }
        setIsActivelyRecording(false);
      };
      recorder.onstop = async () => {
        if (chunkUploadIntervalRef.current) {
          clearInterval(chunkUploadIntervalRef.current);
          chunkUploadIntervalRef.current = null;
        }
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }

        setChunkUploadMessage("Finalizing recording...");

        const flushWaitStart = Date.now();
        const FLUSH_WAIT_TIMEOUT = 30000;
        while (chunkFlushInProgressRef.current) {
          if (Date.now() - flushWaitStart > FLUSH_WAIT_TIMEOUT) {
            console.warn("[Recording] Flush wait timeout reached (30s), proceeding with assembly");
            chunkFlushInProgressRef.current = false;
            break;
          }
          await new Promise((r) => setTimeout(r, 100));
        }

        const remainingChunks = recordingChunksRef.current;
        const hadPriorChunkUploads = chunkIndexRef.current > 0;

        if (remainingChunks.length > 0 && activeRecordingIdRef.current) {
          const finalChunkBlob = new Blob(remainingChunks, { type: remainingChunks[0]?.type || "video/webm" });
          const finalChunkIdx = chunkIndexRef.current;
          chunkIndexRef.current++;
          setChunkUploadMessage(`Uploading final chunk ${finalChunkIdx + 1}...`);
          const finalOk = await uploadChunkToServer(finalChunkBlob, activeRecordingIdRef.current, finalChunkIdx, 5);
          if (finalOk) {
            recordingChunksRef.current = [];
            lastChunkUploadedRef.current = 0;
            console.log(`[ChunkUpload] Final chunk ${finalChunkIdx} uploaded successfully`);
          } else {
            console.error(`[ChunkUpload] Final chunk ${finalChunkIdx} failed after all retries`);
          }
        }

        const totalChunksUploaded = chunkIndexRef.current;

        mediaRecorderRef.current = null;
        recordingStreamRef.current?.getTracks().forEach((t) => t.stop());
        recordingStreamRef.current = null;
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
        }

        failedAssemblyRecordingIdRef.current = null;

        if (activeRecordingIdRef.current && totalChunksUploaded > 0) {
          setIsUploading(true);
          setUploadStatus("uploading");
          setUploadProgress(50);
          setChunkUploadMessage(`Assembling ${totalChunksUploaded} chunks...`);
          const recId = activeRecordingIdRef.current;
          try {
            const assembleRes = await fetch(`/api/recordings/${recId}/assemble-chunks`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
            });
            const result = await assembleRes.json();
            if (assembleRes.ok && result.assembled) {
              setUploadProgress(100);
              setUploadStatus("success");
              setChunkUploadMessage("");
              toast({ title: "Recording saved successfully" });
              queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"], refetchType: "none" });
              queryClient.invalidateQueries({ queryKey: ["/api/recordings"] });
            } else {
              setUploadStatus("failed");
              setChunkUploadMessage("");
              failedAssemblyRecordingIdRef.current = recId;
              toast({ title: "Recording saved as partial", description: "Chunks uploaded. Use retry to assemble.", variant: "destructive" });
            }
          } catch {
            setUploadStatus("failed");
            setChunkUploadMessage("");
            failedAssemblyRecordingIdRef.current = recId;
            toast({ title: "Upload finalization failed", description: "Use retry to assemble chunks.", variant: "destructive" });
          } finally {
            setIsUploading(false);
            activeRecordingIdRef.current = null;
            setTimeout(() => setUploadProgress(0), 5000);
          }
        } else if (activeRecordingIdRef.current && remainingChunks.length > 0) {
          const mimeForBlob = recorder.mimeType || selectedMime;
          const blob = new Blob(remainingChunks, { type: mimeForBlob });
          const recId = activeRecordingIdRef.current;
          setChunkUploadMessage("Uploading recording...");
          const chunkOk = await uploadChunkToServer(blob, recId, 0, 5);
          if (chunkOk) {
            chunkIndexRef.current = 1;
            setIsUploading(true);
            setUploadStatus("uploading");
            setUploadProgress(50);
            setChunkUploadMessage("Assembling recording...");
            try {
              const assembleRes = await fetch(`/api/recordings/${recId}/assemble-chunks`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
              });
              const result = await assembleRes.json();
              if (assembleRes.ok && result.assembled) {
                setUploadProgress(100);
                setUploadStatus("success");
                setChunkUploadMessage("");
                toast({ title: "Recording saved successfully" });
                queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"], refetchType: "none" });
                queryClient.invalidateQueries({ queryKey: ["/api/recordings"] });
              } else {
                setUploadStatus("failed");
                setChunkUploadMessage("");
                failedAssemblyRecordingIdRef.current = recId;
                toast({ title: "Recording saved as partial", description: "Use retry to assemble.", variant: "destructive" });
              }
            } catch {
              setUploadStatus("failed");
              setChunkUploadMessage("");
              failedAssemblyRecordingIdRef.current = recId;
              toast({ title: "Upload finalization failed", variant: "destructive" });
            } finally {
              setIsUploading(false);
              activeRecordingIdRef.current = null;
              setTimeout(() => setUploadProgress(0), 5000);
            }
          } else {
            setChunkUploadMessage("");
            uploadRecordingBlob(blob, recId);
          }
        } else if (activeRecordingIdRef.current) {
          setChunkUploadMessage("");
          toast({ title: "Recording empty", description: "No media data was captured.", variant: "destructive" });
          fetch(`/api/recordings/${activeRecordingIdRef.current}/confirm-upload`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ failed: true }),
          }).catch(() => {});
        }
      };
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      recordingStartTimeRef.current = Date.now();
      chunkIndexRef.current = 0;
      lastChunkUploadedRef.current = 0;
      setRecordingDuration(0);
      setUploadStatus("idle");
      setIsActivelyRecording(true);

      recordingTimerRef.current = setInterval(() => {
        if (recordingStartTimeRef.current > 0) {
          setRecordingDuration(Math.floor((Date.now() - recordingStartTimeRef.current) / 1000));
        }
      }, 1000);

      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
          flushChunksToServer();
        }
      }, 3000);

      chunkUploadIntervalRef.current = setInterval(() => {
        flushChunksToServer();
      }, 15000);

      console.log(`Recording started: ${audioSourceCount} audio sources, ${hasVideo ? "with" : "no"} video, mime: ${selectedMime}`);
    } catch (err) {
      console.error("Client-side recording failed to start:", err);
      toast({ title: "Recording failed to start", description: String(err), variant: "destructive" });
      if (activeRecordingIdRef.current) {
        fetch(`/api/recordings/${activeRecordingIdRef.current}/confirm-upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ failed: true }),
        }).catch(() => {});
      }
    }
  }, [uploadRecordingBlob, toast, flushChunksToServer, gatherMediaTracks]);

  const stopClientRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsActivelyRecording(false);
  }, []);

  const autoRecordTriggeredRef = useRef(false);
  const autoRecordTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoRecordTimeoutRef.current) {
        clearTimeout(autoRecordTimeoutRef.current);
        autoRecordTimeoutRef.current = null;
      }
      if (chunkUploadIntervalRef.current) {
        clearInterval(chunkUploadIntervalRef.current);
        chunkUploadIntervalRef.current = null;
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }
      recordingStreamRef.current?.getTracks().forEach((t) => t.stop());
      recordingStreamRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (
      data?.recording?.isRecording &&
      data.recording.recordingId &&
      data.session?.status === "LIVE" &&
      !mediaRecorderRef.current &&
      !autoRecordTriggeredRef.current &&
      activeToken &&
      isSessionOwner
    ) {
      autoRecordTriggeredRef.current = true;
      activeRecordingIdRef.current = data.recording.recordingId;
      if (autoRecordTimeoutRef.current) clearTimeout(autoRecordTimeoutRef.current);
      autoRecordTimeoutRef.current = setTimeout(() => {
        autoRecordTimeoutRef.current = null;
        startClientRecording();
      }, 5000);
    }
    if (!data?.recording?.isRecording) {
      autoRecordTriggeredRef.current = false;
    }
  }, [data?.recording?.isRecording, data?.recording?.recordingId, data?.session?.status, activeToken, startClientRecording, isSessionOwner]);

  const startRecording = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/sessions/${sessionId}/recording/start`);
      return await res.json() as { ok: boolean; recordingId: string };
    },
    onSuccess: (result) => {
      activeRecordingIdRef.current = result.recordingId;
      toast({ title: t("session.recordingStarted") });
      // Update cache directly — invalidating would trigger a refetch that can unmount stage
      queryClient.setQueryData<SessionState>(["/api/sessions", sessionId, "state"], (old) => {
        if (!old) return old;
        return { ...old, recording: { isRecording: true, recordingId: result.recordingId, startedAt: new Date().toISOString() } };
      });
      setTimeout(() => startClientRecording(), 500);
    },
    onError: (err: any) => {
      toast({ title: t("session.recordingError"), description: err.message, variant: "destructive" });
    },
  });

  const stopRecording = useMutation({
    mutationFn: async () => {
      stopClientRecording();
      try {
        const res = await apiRequest("POST", `/api/sessions/${sessionId}/recording/stop`);
        return await res.json() as { ok: boolean; recordingId: string; durationSeconds: number | null };
      } catch (err: any) {
        if (err.message?.includes("No active recording")) {
          return { ok: true, recordingId: "", durationSeconds: null };
        }
        throw err;
      }
    },
    onSuccess: () => {
      toast({ title: t("session.recordingStopped"), description: "Uploading recording to cloud storage..." });
      // Update cache directly — invalidating would trigger a refetch that can unmount stage
      queryClient.setQueryData<SessionState>(["/api/sessions", sessionId, "state"], (old) => {
        if (!old) return old;
        return { ...old, recording: { isRecording: false, recordingId: null, startedAt: null } };
      });
    },
    onError: (err: any) => {
      toast({ title: t("session.recordingError"), description: err.message, variant: "destructive" });
    },
  });

  const activeMessages = useMemo(() => localMessages.filter((m) => !m.deletedAt), [localMessages]);
  const canPublish = useMemo(() => {
    if (!data) return false;
    const { permissions } = data;
    return tokenData?.permissions?.canPublish || permissions.isOwner || (permissions.isCohost && permissions.cohostStatus === "ACCEPTED") || false;
  }, [data, tokenData?.permissions?.canPublish]);

  if (wasKicked) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center" data-testid="panel-kicked">
        <div className="glass-panel p-8 text-center max-w-md">
          <ShieldAlert className="mx-auto mb-4 h-12 w-12 text-destructive" />
          <h2 className="text-lg font-semibold mb-2">{t("session.removedFromSession", "Removed from Session")}</h2>
          <p className="text-sm text-muted-foreground mb-4">
            {t("session.removedFromSessionDesc", "You have been removed from this session by a moderator.")}
          </p>
          <Link href="/">
            <Button data-testid="button-go-home">{t("session.goHome", "Go Home")}</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-pulse rounded-full bg-primary/20" />
          <p className="text-muted-foreground">{t("session.connecting")}</p>
        </div>
      </div>
    );
  }

  if (!data || isError) {
    return (
      <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
        <div className="glass-panel p-8 text-center">
          <p className="text-muted-foreground">{t("session.notFound")}</p>
          <Button className="mt-4" onClick={() => navigate("/classes")} data-testid="button-browse">
            {t("session.browseClasses")}
          </Button>
        </div>
      </div>
    );
  }

  const { session, class: cls, provider, permissions, cohosts, shareUrl } = data;
  const isLive = session.status === "LIVE";
  const shouldShowStage = isLive && !!tokenData?.token && !!tokenData?.url;
  const stageStable = activeToken !== null && (sessionWasLiveRef.current || shouldShowStage) && session.status !== "ENDED";

  const hasPendingInvite = permissions.isCohost && permissions.cohostStatus === "INVITED";
  const showFloatingChat = isFullscreen || isScreenSharing;

  return (
    <div
      ref={containerRef}
      className={`flex flex-col lg:flex-row overflow-hidden bg-background ${isFullscreen ? 'h-screen' : isLandscapeMobile ? 'h-screen' : 'h-[calc(100vh-3.5rem)]'}`}
      data-testid="live-session-container"
    >
      {hasPendingInvite && !isLandscapeMobile && !isFullscreen && (
        <div className="flex items-center justify-between gap-3 bg-primary/10 border-b border-primary/20 backdrop-blur-sm px-4 py-2.5" data-testid="cohost-invite-banner">
          <p className="text-sm text-primary">
            {t("session.cohostInviteMessage", { name: provider.name })}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              onClick={() => acceptInvite.mutate()}
              disabled={acceptInvite.isPending}
              data-testid="button-accept-cohost"
            >
              {t("session.acceptInvite")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => declineInvite.mutate()}
              disabled={declineInvite.isPending}
              data-testid="button-decline-cohost"
            >
              {t("session.declineInvite")}
            </Button>
          </div>
        </div>
      )}
      <div className={`flex flex-col min-h-0 flex-1 transition-all duration-300`}>
      <div className="relative live-stage live-stage-shadow flex-1 min-h-0">
        {stageStable && activeToken ? (
          <VideoStage
            token={activeToken.token}
            url={activeToken.url}
            isOwner={permissions.isOwner}
            canPublish={canPublish}
            isCohost={!!(permissions.isCohost && permissions.cohostStatus === "ACCEPTED")}
            sessionId={sessionId || ""}
            isProvider={!!user && (user.role === "PROVIDER" || user.role === "ADMIN" || user.role === "SUPER_ADMIN")}
            onScreenShareChange={handleScreenShareChange}
          />
        ) : (
          <StreamPlaceholder
            isLive={isLive}
            message={
              livekitNotConfigured
                ? t("session.livekitNotConfigured")
                : tokenError
                  ? t("session.streamConnectionError", { defaultValue: "Unable to connect to stream. Retrying..." })
                  : tokenLoading
                    ? t("session.connectingStream")
                    : undefined
            }
          />
        )}

        <div className="absolute left-2 sm:left-3 top-2 sm:top-3 z-10 flex items-center gap-1 sm:gap-1.5">
          {!isFullscreen && (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => navigate(`/classes/${cls.id}`)}
              className="h-7 w-7 live-overlay-btn no-default-hover-elevate"
              data-testid="button-back-live"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
            </Button>
          )}
          {isLive && (
            <Badge className="glass-badge-live text-[10px] px-2 py-0.5 rounded-full no-default-hover-elevate flex items-center gap-1">
              <LiveBroadcastIcon size={13} color="white" />
              LIVE
            </Badge>
          )}
          <Button
            size="icon"
            variant="ghost"
            onClick={toggleFullscreen}
            className="h-7 w-7 live-overlay-btn no-default-hover-elevate"
            data-testid="button-fullscreen"
          >
            {isFullscreen ? <Minimize className="h-3.5 w-3.5" /> : <Maximize className="h-3.5 w-3.5" />}
          </Button>
          {shareUrl && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                try {
                  const textArea = document.createElement("textarea");
                  textArea.value = shareUrl;
                  textArea.style.position = "fixed";
                  textArea.style.left = "-9999px";
                  textArea.style.top = "-9999px";
                  textArea.style.opacity = "0";
                  document.body.appendChild(textArea);
                  textArea.focus();
                  textArea.select();
                  document.execCommand("copy");
                  document.body.removeChild(textArea);
                  setShareCopied(true);
                  setTimeout(() => setShareCopied(false), 2000);
                } catch {
                  navigator.clipboard?.writeText(shareUrl).then(() => {
                    setShareCopied(true);
                    setTimeout(() => setShareCopied(false), 2000);
                  }).catch(() => {});
                }
              }}
              className="h-7 px-2.5 live-overlay-btn no-default-hover-elevate gap-1 text-[11px]"
              data-testid="button-share-session"
            >
              {shareCopied ? <Check className="h-3 w-3 text-green-400" /> : <Share2 className="h-3 w-3" />}
              <span className="hidden sm:inline">{shareCopied ? t("common.copied") || "Copied!" : t("common.share") || "Share"}</span>
            </Button>
          )}
        </div>

        {isLive && permissions.isOwner && (
          <div className="absolute right-2 sm:right-4 top-2 sm:top-4 z-10">
            <CoHostPanel
              sessionId={session.id}
              isOwner={permissions.isOwner}
              cohosts={cohosts || []}
            />
          </div>
        )}

        {chatCollapsed && !isFullscreen && (
          <div className="absolute left-2 sm:left-4 top-12 sm:top-14 z-10">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => handleChatCollapse(false)}
              className="h-10 w-10 live-overlay-btn no-default-hover-elevate"
              data-testid="button-toggle-chat"
            >
              <PanelRightOpen className="h-5 w-5" />
            </Button>
            {activeMessages.length > 0 && (
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] text-primary-foreground font-bold">
                {activeMessages.length > 99 ? "99" : activeMessages.length}
              </span>
            )}
          </div>
        )}

        <div className={`absolute ${isLandscapeMobile || isFullscreen ? 'bottom-1 left-1 right-1' : 'bottom-1.5 sm:bottom-2 left-2 sm:left-3 right-2 sm:right-3'} z-10`}>
          <div className="flex items-end justify-between gap-2">
            <div className={`live-info-bar min-w-0 max-w-[60%] ${isLandscapeMobile || isFullscreen ? 'px-2.5 py-1' : 'px-3.5 py-1.5 sm:px-4 sm:py-2'}`}>
              <div className="flex items-center gap-2">
                <h2 className={`font-semibold text-white truncate ${isLandscapeMobile || isFullscreen ? 'text-[11px]' : 'text-sm sm:text-base'}`} data-testid="text-live-title">
                  {cls.title}
                </h2>
                <Badge variant="outline" className="text-[10px] h-5 px-1.5 rounded-full bg-white/5 border-white/20 text-white/70 shrink-0" data-testid="badge-viewer-count-stage">
                  <Users className="mr-0.5 h-2.5 w-2.5" />
                  {data.viewerCount || 0}
                </Badge>
              </div>
              {!isLandscapeMobile && !isFullscreen && (
                <p className="text-[11px] sm:text-xs text-white/50 truncate mt-0.5">
                  {t("session.by", { name: provider.name })}
                  {cohosts && cohosts.length > 0 && (
                    <span className="ml-1 text-white/40">
                      {" + "}{cohosts.map(c => c.userName).join(", ")}
                    </span>
                  )}
                </p>
              )}
            </div>
            {chunkUploadMessage && isActivelyRecording && uploadStatus === "idle" && (
              <Badge variant="outline" className="text-[10px] h-6 px-2 rounded-full bg-blue-500/20 border-blue-500/40 text-blue-300 animate-pulse" data-testid="badge-chunk-progress">
                <Loader2 className="mr-1 h-2.5 w-2.5 animate-spin" /> {chunkUploadMessage}
              </Badge>
            )}
            {uploadStatus === "uploading" && (
              <Badge variant="outline" className="text-[10px] h-6 px-2 rounded-full bg-amber-500/20 border-amber-500/40 text-amber-300 animate-pulse" data-testid="badge-upload-progress">
                <Loader2 className="mr-1 h-2.5 w-2.5 animate-spin" /> {chunkUploadMessage || `Uploading ${uploadProgress}%`}
              </Badge>
            )}
            {uploadStatus === "retrying" && (
              <Badge variant="outline" className="text-[10px] h-6 px-2 rounded-full bg-orange-500/20 border-orange-500/40 text-orange-300 animate-pulse" data-testid="badge-upload-retrying">
                <Loader2 className="mr-1 h-2.5 w-2.5 animate-spin" /> Retrying...
              </Badge>
            )}
            {uploadStatus === "success" && (
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="text-[10px] h-6 px-2 rounded-full bg-green-500/20 border-green-500/40 text-green-300" data-testid="badge-upload-success">
                  <Check className="mr-1 h-2.5 w-2.5" /> Saved
                </Badge>
                <Link href="/provider" className="text-[10px] text-primary/80 hover:text-primary underline" data-testid="link-view-recording">
                  View Recording
                </Link>
              </div>
            )}
            {uploadStatus === "failed" && (
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="text-[10px] h-6 px-2 rounded-full bg-red-500/20 border-red-500/40 text-red-300" data-testid="badge-upload-failed">
                  <AlertTriangle className="mr-1 h-2.5 w-2.5" /> Upload Failed
                </Badge>
                {activeRecordingIdRef.current && lastRecordingBlobRef.current && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-[10px] h-6 px-2 rounded-full bg-amber-500/10 border-amber-500/40 text-amber-300 hover:bg-amber-500/20"
                    data-testid="button-retry-upload"
                    onClick={async () => {
                      const blob = lastRecordingBlobRef.current;
                      const recId = activeRecordingIdRef.current;
                      if (blob && recId) {
                        uploadRecordingBlob(blob, recId);
                      }
                    }}
                  >
                    <RefreshCw className="mr-1 h-2.5 w-2.5" /> Retry
                  </Button>
                )}
                {failedAssemblyRecordingIdRef.current && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-[10px] h-6 px-2 rounded-full bg-amber-500/10 border-amber-500/40 text-amber-300 hover:bg-amber-500/20"
                    data-testid="button-retry-assembly"
                    onClick={async () => {
                      const recId = failedAssemblyRecordingIdRef.current;
                      if (!recId) return;
                      setIsUploading(true);
                      setUploadStatus("uploading");
                      setUploadProgress(50);
                      try {
                        const res = await fetch(`/api/recordings/${recId}/assemble-chunks`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                        });
                        const result = await res.json();
                        if (res.ok && result.assembled) {
                          setUploadProgress(100);
                          setUploadStatus("success");
                          failedAssemblyRecordingIdRef.current = null;
                          toast({ title: "Recording assembled successfully" });
                          queryClient.invalidateQueries({ queryKey: ["/api/sessions", sessionId, "state"], refetchType: "none" });
                          queryClient.invalidateQueries({ queryKey: ["/api/recordings"] });
                        } else {
                          setUploadStatus("failed");
                          toast({ title: "Assembly retry failed", variant: "destructive" });
                        }
                      } catch {
                        setUploadStatus("failed");
                        toast({ title: "Assembly retry failed", variant: "destructive" });
                      } finally {
                        setIsUploading(false);
                        setTimeout(() => setUploadProgress(0), 5000);
                      }
                    }}
                  >
                    <RefreshCw className="mr-1 h-2.5 w-2.5" /> Retry Assembly
                  </Button>
                )}
              </div>
            )}
            {(permissions.isOwner || permissions.canEndSession) && isLive && data.recording !== undefined && (
              <div className="flex items-center gap-1.5 shrink-0">
                {data.recording?.isRecording ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => stopRecording.mutate()}
                    disabled={stopRecording.isPending}
                    className="text-xs h-8 px-3 rounded-full live-rec-btn bg-red-500/20 border-red-500/60 text-red-300 hover:bg-red-500/30 hover:text-red-200 no-default-hover-elevate shadow-[0_0_12px_rgba(239,68,68,0.3)]"
                    data-testid="button-stop-recording"
                  >
                    <span className="relative flex h-2.5 w-2.5 mr-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
                    </span>
                    <RecordingTimer startedAt={data.recording.startedAt} />
                    <span className="ml-1.5 hidden sm:inline">{t("session.stopRecording")}</span>
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => startRecording.mutate()}
                    disabled={startRecording.isPending}
                    className="text-xs h-8 px-3 rounded-full live-rec-btn bg-white/10 border-white/30 text-white hover:bg-red-500/20 hover:border-red-500/50 hover:text-red-200 no-default-hover-elevate transition-colors"
                    data-testid="button-start-recording"
                  >
                    <Circle className="mr-1.5 h-3 w-3 text-red-400" />
                    <span>{t("session.startRecording")}</span>
                  </Button>
                )}
                {permissions.canEndSession && (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => endSession.mutate()}
                    disabled={endSession.isPending}
                    className="text-[11px] h-7 px-2.5 rounded-full"
                    data-testid="button-end-session"
                  >
                    {endSession.isPending ? (
                      <><Loader2 className="mr-1 h-2.5 w-2.5 animate-spin" /><span>Saving...</span></>
                    ) : (
                      <><StopCircle className="mr-1 h-2.5 w-2.5" /><span className="hidden sm:inline">{t("session.endSession")}</span><span className="sm:hidden">End</span></>
                    )}
                  </Button>
                )}
              </div>
            )}
            {!permissions.isOwner && permissions.canEndSession && isLive && data.recording === undefined && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => endSession.mutate()}
                disabled={endSession.isPending}
                className="text-[11px] h-7 px-2.5 rounded-full"
                data-testid="button-end-session"
              >
                {endSession.isPending ? (
                  <><Loader2 className="mr-1 h-2.5 w-2.5 animate-spin" /><span>Saving...</span></>
                ) : (
                  <><StopCircle className="mr-1 h-2.5 w-2.5" /><span className="hidden sm:inline">{t("session.endSession")}</span><span className="sm:hidden">End</span></>
                )}
              </Button>
            )}
          </div>
        </div>

        <CaptionOverlay caption={caption} chatCollapsed={chatCollapsed} />

        {floatingReactions.map((r: { id: number; type: string; x: number }) => {
          const reactionDef = REACTION_TYPES.find((rt) => rt.type === r.type);
          if (!reactionDef) return null;
          const Icon = reactionDef.icon;
          return (
            <div
              key={r.id}
              className="absolute bottom-20 pointer-events-none z-20"
              style={{
                left: `${r.x}%`,
                animation: "floatUp 2s ease-out forwards",
              }}
            >
              <Icon className={`h-6 w-6 ${reactionDef.color}`} />
            </div>
          );
        })}

        {showFloatingChat && (
          <FloatingChat
            messages={activeMessages}
            chatMessage={chatMessage}
            setChatMessage={setChatMessage}
            sendMessage={sendMessage}
            sendReaction={sendReaction}
            deleteMessage={deleteMessage}
            permissions={permissions}
            isLive={isLive}
            isAuthenticated={!!user}
            sessionId={sessionId || ""}
            t={t}
          />
        )}

      </div>

      {!isLandscapeMobile && !isFullscreen && (
        <CaptionToolbar caption={caption} isOwner={permissions.isOwner} isLive={isLive} />
      )}
      </div>

      {!chatCollapsed && !isFullscreen && (
        <div
          className="hidden lg:flex items-center justify-center w-1.5 cursor-col-resize hover:bg-primary/20 active:bg-primary/30 transition-colors group shrink-0"
          onMouseDown={handleResizeStart}
          data-testid="chat-resize-handle"
        >
          <div className="w-0.5 h-8 rounded-full bg-border group-hover:bg-primary/50 transition-colors" />
        </div>
      )}
      {!isFullscreen && (<div
        data-chat-panel
        className={`flex flex-col live-chat-panel transition-all duration-300 ease-in-out shrink-0 ${chatCollapsed ? 'h-0 overflow-hidden lg:h-full lg:w-0' : 'min-h-[10rem] flex-1 lg:flex-none lg:h-full overflow-hidden min-w-0'}`}
        style={!chatCollapsed && isDesktop ? { width: `${chatWidth}px` } : undefined}
      >
        <div className="live-chat-header flex items-center justify-between gap-2 p-2 sm:p-3 shrink-0">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-primary/60" />
            <span className="text-xs sm:text-sm font-medium">{t("session.liveChat")}</span>
          </div>
          <div className="flex items-center gap-1.5">
            {(permissions.isOwner || (permissions.isCohost && permissions.cohostStatus === "ACCEPTED") || permissions.canModerate) && (
              <Button
                size="sm"
                variant={showParticipants ? "secondary" : "outline"}
                onClick={() => {
                  setShowParticipants((v) => !v);
                  if (!showParticipants && wsRef.current?.readyState === WebSocket.OPEN) {
                    wsRef.current.send(JSON.stringify({ type: "participants:request" }));
                  }
                }}
                className="h-6 px-2 text-[10px] sm:text-xs gap-1"
                data-testid="button-participants"
              >
                <Users className="h-3 w-3" />
                {data.viewerCount || 0}
              </Button>
            )}
            {!(permissions.isOwner || (permissions.isCohost && permissions.cohostStatus === "ACCEPTED") || permissions.canModerate) && (
              <Badge variant="outline" className="text-[10px] sm:text-xs">
                <Users className="mr-1 h-3 w-3" />
                {data.viewerCount || 0}
              </Badge>
            )}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => handleChatCollapse(true)}
              className="h-7 w-7 no-default-hover-elevate"
              data-testid="button-collapse-chat"
            >
              <PanelRightClose className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {showParticipants && (permissions.isOwner || (permissions.isCohost && permissions.cohostStatus === "ACCEPTED") || permissions.canModerate) && (
          <div className="border-b p-2 sm:p-3" data-testid="panel-participants">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium">{t("session.participants", "Participants")} ({participants.length})</span>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setShowParticipants(false)}
                className="h-5 w-5 no-default-hover-elevate"
                data-testid="button-close-participants"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            <ScrollArea className="max-h-48">
              <div className="space-y-1">
                {participants.length === 0 && (
                  <p className="text-[10px] text-muted-foreground text-center py-2">No participants</p>
                )}
                {participants.map((p) => (
                  <div key={p.userId} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/50 group" data-testid={`participant-${p.userId}`}>
                    <Avatar className="h-5 w-5 shrink-0">
                      <AvatarFallback className="text-[8px] bg-muted">
                        {p.displayName?.[0]?.toUpperCase() || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-[11px] truncate flex-1">{p.displayName}</span>
                    <Badge
                      variant={p.role === "host" ? "default" : p.role === "co-host" ? "secondary" : "outline"}
                      className="text-[8px] h-4 px-1 shrink-0"
                      data-testid={`participant-role-${p.userId}`}
                    >
                      {p.role}
                    </Badge>
                    {p.role !== "host" && (permissions.canKickUsers || permissions.canMuteUsers) && (
                      <div className="flex items-center gap-0.5 invisible group-hover:visible shrink-0">
                        {permissions.canMuteUsers && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-5 w-5 no-default-hover-elevate"
                            onClick={() => muteParticipant.mutate(p.userId)}
                            disabled={muteParticipant.isPending}
                            title={t("session.muteParticipant", "Mute")}
                            data-testid={`button-mute-${p.userId}`}
                          >
                            <MicOff className="h-3 w-3 text-muted-foreground" />
                          </Button>
                        )}
                        {permissions.canKickUsers && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-5 w-5 no-default-hover-elevate text-destructive"
                            onClick={() => kickParticipant.mutate(p.userId)}
                            disabled={kickParticipant.isPending}
                            title={t("session.kickParticipant", "Remove")}
                            data-testid={`button-kick-${p.userId}`}
                          >
                            <UserX className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        <div className="relative flex-1 min-w-0 overflow-hidden">
        <ScrollArea
          className="h-full p-2 sm:p-3 min-w-0 overflow-hidden"
          onScrollCapture={(e: React.UIEvent<HTMLDivElement>) => {
            const el = e.currentTarget.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
            if (el) {
              const threshold = 80;
              isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
              if (isNearBottomRef.current) setMainChatNewMessages(0);
            }
          }}
        >
          <div className="space-y-2 sm:space-y-3 min-w-0 w-full">
            {activeMessages.map((msg) => (
              <div
                key={msg.id}
                className="group flex items-start gap-1.5 sm:gap-2 min-w-0"
                data-testid={`chat-message-${msg.id}`}
              >
                <Avatar className="h-6 w-6 sm:h-7 sm:w-7 shrink-0">
                  <AvatarFallback className="text-[9px] sm:text-[10px] bg-muted">
                    {msg.userName?.[0]?.toUpperCase() || "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] sm:text-xs font-medium truncate">{msg.userName}</span>
                    <span className="text-[9px] sm:text-[10px] text-muted-foreground">
                      {format(new Date(msg.createdAt), "h:mm a")}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground break-words overflow-hidden">{msg.text}</p>
                </div>
                {permissions.canModerate && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="invisible h-5 w-5 sm:h-6 sm:w-6 shrink-0 group-hover:visible"
                    onClick={() => deleteMessage.mutate(msg.id)}
                    data-testid={`button-delete-msg-${msg.id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
        </ScrollArea>
        {mainChatNewMessages > 0 && (
          <button
            onClick={scrollMainChatToBottom}
            className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary/90 hover:bg-primary text-white text-xs font-medium shadow-lg transition-colors"
            data-testid="button-main-new-messages"
          >
            <ArrowDown className="h-3.5 w-3.5" />
            {mainChatNewMessages} new {mainChatNewMessages === 1 ? "message" : "messages"}
          </button>
        )}
        </div>

        {permissions.canReact && isLive && (
          <div className="flex items-center justify-center gap-0.5 sm:gap-1 border-t border-white/[0.06] px-2 sm:px-3 py-1.5 sm:py-2 flex-wrap">
            {REACTION_TYPES.map((r) => (
              <Button
                key={r.type}
                size="icon"
                variant="ghost"
                className="h-7 w-7 sm:h-8 sm:w-8"
                onClick={() => sendReaction.mutate(r.type)}
                data-testid={`button-reaction-${r.type}`}
              >
                <r.icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${r.color}`} />
              </Button>
            ))}
          </div>
        )}

        {permissions.canChat && isLive && (
          <div className="border-t border-white/[0.06] bg-white/[0.01] p-2 sm:p-3">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage.mutate();
              }}
              className="flex items-center gap-1.5 sm:gap-2"
            >
              <Input
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                placeholder={t("session.sendAMessage")}
                className="flex-1 h-8 sm:h-9 text-xs sm:text-sm glass-input"
                maxLength={500}
                data-testid="input-chat"
              />
              <Button
                type="submit"
                size="icon"
                className="h-8 w-8 sm:h-9 sm:w-9"
                disabled={!chatMessage.trim() || sendMessage.isPending}
                data-testid="button-send-chat"
              >
                <Send className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
            </form>
          </div>
        )}

        {!user && isLive && (
          <div className="border-t border-white/[0.06] bg-white/[0.01] p-2.5 sm:p-3" data-testid="guest-chat-prompt">
            <div className="flex flex-col items-center gap-2 py-1">
              <p className="text-[11px] sm:text-xs text-muted-foreground text-center">
                {t("session.signInToChat", "Sign in to join the conversation")}
              </p>
              <div className="flex items-center gap-2">
                <Link href={`/login?redirect=/live/${sessionId}`}>
                  <Button size="sm" className="h-7 sm:h-8 text-[11px] sm:text-xs btn-gradient-capsule" data-testid="button-guest-login">
                    <LogIn className="mr-1.5 h-3 w-3 sm:h-3.5 sm:w-3.5" />
                    {t("session.logIn", "Log in")}
                  </Button>
                </Link>
                <Link href={`/login?redirect=/live/${sessionId}`}>
                  <Button size="sm" variant="outline" className="h-7 sm:h-8 text-[11px] sm:text-xs" data-testid="button-guest-signup">
                    <UserPlus className="mr-1.5 h-3 w-3 sm:h-3.5 sm:w-3.5" />
                    {t("session.createAccount", "Create account")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {!isLive && (
          <div className="border-t border-white/[0.06] p-2 sm:p-3 text-center">
            <p className="text-[10px] sm:text-xs text-muted-foreground">{t("session.chatDisabled")}</p>
          </div>
        )}
      </div>)}

      <style>{`
        @keyframes floatUp {
          0% { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-200px) scale(1.5); }
        }
      `}</style>
    </div>);
}

function RecordingTimer({ startedAt }: { startedAt: string | null }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startedAt) return;
    const start = new Date(startedAt).getTime();
    const update = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = elapsed % 60;
  const timeStr = h > 0
    ? `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
    : `${m}:${s.toString().padStart(2, "0")}`;

  return <span className="font-mono text-[11px]" data-testid="text-recording-timer">{timeStr}</span>;
}
